// Unified Admin Panel — BrandMark · LeadGen · Corpus50 · SmartVault
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Loader2, RefreshCw, KeyRound, Users, TrendingUp, DollarSign, Shield, Edit3, Star, Trash2 } from 'lucide-react';
import api from '../../utils/api';

const TABS = [
  { id: 'brandmark', label: '🎨 BrandMark',   color: 'amber',   base: '/admin/brandmark' },
  { id: 'leadgen',   label: '🎯 LeadGen',     color: 'cyan',    base: '/admin/leadgen' },
  { id: 'corpus50',  label: '💰 Corpus50',    color: 'orange',  base: '/admin/corpus50' },
  { id: 'vault',     label: '🔐 Smart Vault', color: 'fuchsia', base: '/admin/vault' },
  { id: 'yojana',    label: '🎯 Yojana Mitra',color: 'emerald', base: '/admin/anant-yojana' },
  { id: 'khata',     label: '📒 Khata',       color: 'amber',   base: '/admin/anant-khata' },
  { id: 'asn',       label: '📰 ASN',         color: 'blue',    base: '/admin/asn' },
  { id: 'health',    label: '💗 Health',      color: 'emerald', base: '/admin/health' },
  { id: 'fieldpro',  label: '🧳 FieldPro',    color: 'violet',  base: '/admin/fieldpro' },
  { id: 'citypro',   label: '🏙️ CityPro',     color: 'teal',    base: '/admin/citypro' },
  { id: 'asm',       label: '📣 Smart Marketing', color: 'fuchsia', base: '/admin/asm' },
  { id: 'social',    label: '📣 Social Share',  color: 'fuchsia', base: '/admin/social-share' },
  { id: 'webshop',   label: '🛍️ WebShop',       color: 'emerald', base: '/admin/webshop' },
  { id: 'marketplace',label:'🛒 Marketplace',   color: 'purple',  base: '/admin/marketplace' },
  { id: 'auditor',   label: '🛰️ Auditor',       color: 'red',     base: '/admin/system-auditor' },
];

export default function AdminNewTools() {
  const [tab, setTab] = useState('brandmark');
  const [dashboards, setDashboards] = useState({});
  const [subs, setSubs] = useState({});
  const [leads, setLeads] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  // Vault reset form
  const [resetUserId, setResetUserId] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [reminderLinks, setReminderLinks] = useState([]);

  const loadDashboards = useCallback(async () => {
    setLoading(true);
    try {
      const [b, l, c, v, y, k, h, fp, cp] = await Promise.all([
        api.get('/admin/brandmark/dashboard').catch(() => ({ data: null })),
        api.get('/admin/leadgen/dashboard').catch(() => ({ data: null })),
        api.get('/admin/corpus50/dashboard').catch(() => ({ data: null })),
        api.get('/admin/vault/dashboard').catch(() => ({ data: null })),
        api.get('/admin/anant-yojana/stats').catch(() => ({ data: null })),
        api.get('/admin/anant-khata/stats').catch(() => ({ data: null })),
        api.get('/admin/health/stats').catch(() => ({ data: null })),
        api.get('/admin/fieldpro/stats').catch(() => ({ data: null })),
        api.get('/admin/citypro/stats').catch(() => ({ data: null })),
      ]);
      setDashboards({ brandmark: b.data, leadgen: l.data, corpus50: c.data, vault: v.data, yojana: y.data, khata: k.data, health: h.data, fieldpro: fp.data, citypro: cp.data });
    } catch (e) { toast.error('Dashboard load failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDashboards(); }, [loadDashboards]);

  const loadSubs = async (t) => {
    if (subs[t]) return;
    if (t === 'yojana') return;   // yojana uses schemes panel, not subs table
    if (t === 'khata') return;    // khata has its own panel
    if (t === 'asn') return;      // asn has its own panel
    if (t === 'health') return;   // health has custom panel
    if (t === 'fieldpro') return; // fieldpro has custom panel
    if (t === 'citypro') return;  // citypro has custom panel
    if (t === 'asm') return;         // asm renders its own admin panel
    if (t === 'social') return;      // social share renders its own admin panel
    if (t === 'webshop') return;     // webshop renders its own admin panel
    if (t === 'marketplace') return; // marketplace renders its own admin panel
    if (t === 'auditor') return;     // auditor renders its own panel
    const cfg = TABS.find((x) => x.id === t);
    try {
      const r = await api.get(`${cfg.base}/subscriptions`);
      setSubs((s) => ({ ...s, [t]: r.data.subscriptions || [] }));
    } catch { toast.error(`${cfg.label} subs load failed`); }
  };

  useEffect(() => { loadSubs(tab); }, [tab]);

  // LeadGen extras
  useEffect(() => {
    if (tab !== 'leadgen') return;
    (async () => {
      try {
        const [l, p] = await Promise.all([
          api.get('/admin/leadgen/leads'),
          api.get('/admin/leadgen/landing-pages'),
        ]);
        setLeads(l.data.leads || []);
        setLandingPages(p.data.landing_pages || []);
      } catch { /* silent */ }
    })();
  }, [tab]);

  // Corpus50 extras
  useEffect(() => {
    if (tab !== 'corpus50') return;
    (async () => {
      try {
        const p = await api.get('/admin/corpus50/payments');
        setPayments(p.data.payments || []);
      } catch { /* silent */ }
    })();
  }, [tab]);

  const doVaultReset = async () => {
    if (!resetUserId.trim()) { toast.error('Enter user ID'); return; }
    if (!window.confirm(`Reset vault for user ${resetUserId}? User's local data will be wiped on next visit (irreversible).`)) return;
    try {
      const r = await api.post('/admin/vault/reset-passphrase', { user_id: resetUserId.trim(), reason: resetReason.trim() });
      toast.success(r.data.message);
      setResetUserId(''); setResetReason('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };

  const broadcastReminder = async () => {
    try {
      const r = await api.post('/admin/corpus50/broadcast-reminder', {});
      setReminderLinks(r.data.reminders || []);
      toast.success(`${r.data.count} reminder links generated!`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Broadcast failed'); }
  };

  const d = dashboards[tab];
  const currentTab = TABS.find((x) => x.id === tab);

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <Link to="/admin" className="text-xs text-slate-400 hover:text-white">← Admin</Link>
            <h1 className="text-3xl font-black bg-gradient-to-r from-amber-300 via-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">🛠️ New Tools Admin</h1>
            <p className="text-xs text-slate-400">Full backend control for BrandMark, LeadGen, Corpus50, Smart Vault</p>
          </div>
          <button onClick={loadDashboards} disabled={loading} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh All</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 flex-wrap overflow-x-auto" data-testid="admin-tools-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-3 py-2 rounded-lg text-xs font-black whitespace-nowrap border-2 ${tab === t.id ? `bg-${t.color}-500 text-black border-${t.color}-300` : `bg-white/5 text-slate-300 border-transparent`}`}>{t.label}</button>
          ))}
        </div>

        {/* Stats grid */}
        {d?.stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid={`admin-stats-${tab}`}>
            {Object.entries(d.stats).filter(([k, v]) => typeof v !== 'object').map(([k, v]) => (
              <div key={k} className={`rounded-xl border border-${currentTab.color}-400/40 bg-${currentTab.color}-500/10 p-3`}>
                <div className={`text-[9px] uppercase tracking-widest text-${currentTab.color}-300 font-black`}>{k.replace(/_/g, ' ')}</div>
                <div className="text-2xl font-black">{typeof v === 'number' ? v.toLocaleString() : String(v)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'leadgen' && <ReferEarnPanel />}
        {tab === 'yojana' && <YojanaSchemesPanel />}
        {tab === 'yojana' && <LandRecordsAdminPanel />}
        {tab === 'yojana' && <CSCSheetSyncPanel />}
        {tab === 'yojana' && <CSCServicesPanel />}
        {tab === 'khata' && <KhataAdminPanel />}
        {tab === 'asn' && <ASNAdminPanel />}
        {tab === 'health' && <HealthAdminPanel />}
        {tab === 'fieldpro' && <FieldProAdminPanel />}
        {tab === 'citypro' && <CityProAdminPanel />}
        {tab === 'asm' && <ASMAdminPanel />}
        {tab === 'social' && <SocialShareAdminPanel />}
        {tab === 'webshop' && <WebShopAdminPanel />}
        {tab === 'marketplace' && <MarketplaceAdminPanel />}
        {tab === 'auditor' && (
          <div className="rounded-2xl border-2 border-red-400/50 bg-gradient-to-br from-red-950/40 to-slate-900 p-6 text-center">
            <div className="text-6xl mb-3">🛰️</div>
            <div className="text-lg font-black mb-2">Cross-tool System Auditor</div>
            <div className="text-xs opacity-80 mb-4">Standalone page with voice command, bulk actions, CSV export, cross-tool scan (CityPro + Vyapar + Health + FieldPro + AT Wallet)</div>
            <a href="/admin/system-auditor" data-testid="admin-goto-auditor" className="inline-block px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-black">Open System Auditor →</a>
          </div>
        )}

        {/* Subscriptions table */}
        {subs[tab] && subs[tab].length > 0 && (
          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4">
            <div className="text-xs uppercase tracking-widest opacity-70 font-black mb-2">Subscriptions ({subs[tab].length})</div>
            <div className="max-h-80 overflow-y-auto text-xs">
              <table className="w-full">
                <thead className="text-left text-[10px] opacity-70 border-b border-slate-700"><tr><th>User</th><th>Plan</th><th>Amount</th><th>Status</th><th>Expires</th></tr></thead>
                <tbody>
                  {subs[tab].slice(0, 100).map((s) => (
                    <tr key={s.id} className="border-b border-slate-800" data-testid={`admin-sub-${s.id}`}>
                      <td className="py-1.5">{s.username}</td>
                      <td>{s.plan_label || s.plan_id}</td>
                      <td>{(s.amount ?? s.monthly_at ?? 0).toLocaleString()} AT</td>
                      <td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-500/30' : 'bg-slate-700'}`}>{s.status}</span></td>
                      <td>{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : s.next_due_at ? new Date(s.next_due_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* LeadGen specific: Leads + Landing Pages */}
        {tab === 'leadgen' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
              <div className="text-xs uppercase tracking-widest text-cyan-300 font-black mb-2">All Leads ({leads.length})</div>
              <div className="max-h-80 overflow-y-auto text-xs space-y-1">
                {leads.slice(0, 100).map((l) => (
                  <div key={l.id} className="p-2 rounded bg-slate-800 border border-slate-700">
                    <b>{l.full_name}</b> · {l.mobile} · {l.city}<br />
                    <span className="opacity-70">{l.business_category} · owner: {l.owner_user_id?.slice(0, 8)}…</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
              <div className="text-xs uppercase tracking-widest text-cyan-300 font-black mb-2">All Landing Pages ({landingPages.length})</div>
              <div className="max-h-80 overflow-y-auto text-xs space-y-1">
                {landingPages.slice(0, 100).map((p) => (
                  <div key={p.slug} className="p-2 rounded bg-slate-800 border border-slate-700">
                    <b>{p.display_name}</b> · @{p.username}<br />
                    <span className="opacity-70">/leadgen/lp/{p.slug} · {p.views || 0} views · {p.theme}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Corpus50 specific: Broadcast reminders + Payment log */}
        {tab === 'corpus50' && (
          <>
            <Corpus50CertThemePanel />
            <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 mb-4">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-2">📨 Bulk WhatsApp EMI Reminders</div>
              <div className="text-xs opacity-80 mb-2">Fetches all active S.I.P. subs whose next EMI is due within 3 days. Returns wa.me links (zero API cost — click each to open).</div>
              <button onClick={broadcastReminder} data-testid="admin-corpus50-broadcast" className="px-4 py-2 rounded-lg bg-amber-400 text-black font-black text-sm">Generate Reminder Links</button>
              {reminderLinks.length > 0 && (
                <div className="mt-3 max-h-60 overflow-y-auto space-y-1 text-xs">
                  {reminderLinks.map((r, i) => (
                    <div key={i} className="p-2 rounded bg-slate-800 flex justify-between items-center">
                      <span>{r.username} · {r.phone || 'no phone'}</span>
                      <a href={r.wa_link} target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-green-500 text-black text-[10px] font-black">Send</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-2">Payments Log ({payments.length})</div>
              <div className="max-h-80 overflow-y-auto text-xs space-y-1">
                {payments.slice(0, 100).map((p) => (
                  <div key={p.id} className="p-2 rounded bg-slate-800 flex justify-between">
                    <span>EMI {p.month_index}/72 · <b>{p.amount.toLocaleString()} AT</b> {p.penalty_pct > 0 && <span className="text-rose-400 text-[10px]"> +{p.penalty_pct}%</span>}</span>
                    <span className="opacity-60">{new Date(p.paid_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Vault specific: Passphrase reset */}
        {tab === 'vault' && (
          <div className="rounded-2xl border-2 border-rose-400/50 bg-rose-500/10 p-4 mb-4" data-testid="admin-vault-reset-panel">
            <div className="text-xs uppercase tracking-widest text-rose-300 font-black mb-2 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset User Passphrase</div>
            <div className="text-xs opacity-80 mb-2">Wipes user&apos;s local vault on next visit (encrypted data lost, by design — user forgot passphrase). User creates fresh vault after.</div>
            <input value={resetUserId} onChange={(e) => setResetUserId(e.target.value)} placeholder="User ID (UUID)" data-testid="admin-vault-reset-userid" className="w-full mb-2 px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            <input value={resetReason} onChange={(e) => setResetReason(e.target.value)} placeholder="Reason (optional)" data-testid="admin-vault-reset-reason" className="w-full mb-2 px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            <button onClick={doVaultReset} data-testid="admin-vault-reset-btn" className="px-4 py-2 rounded-lg bg-rose-500 text-white font-black text-sm">Set Reset Flag</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ===== Live Refer & Earn Dashboard (Race to 10K) =====
function ReferEarnPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admin/leadgen/refer/dashboard'); setData(r.data); }
    catch (e) { toast.error('Failed to load refer dashboard'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const pct = Math.min(100, (data.claimed_count / data.cap) * 100);

  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/15 via-pink-500/10 to-purple-500/15 p-4 mb-4" data-testid="admin-refer-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🚀 Refer &amp; Earn · Race to 10K</div>
          <div className="text-xs opacity-90 mt-0.5">Live signup counter + top 10 referrers · {data.active ? <span className="text-emerald-300 font-black">ACTIVE</span> : <span className="text-rose-300 font-black">CLOSED</span>}</div>
        </div>
        <button onClick={load} disabled={loading} data-testid="admin-refer-refresh" className="px-2 py-1 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/15 p-3">
          <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black">Claimed</div>
          <div className="text-2xl font-black" data-testid="admin-refer-claimed">{data.claimed_count.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/15 p-3">
          <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black">Remaining</div>
          <div className="text-2xl font-black">{data.remaining.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/15 p-3">
          <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-black">AT Distributed</div>
          <div className="text-2xl font-black">{data.total_at_distributed.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/15 p-3">
          <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 font-black">Bonus / signup</div>
          <div className="text-2xl font-black">{data.bonus_at} + {data.bonus_at}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] mb-1"><span className="opacity-70">Progress toward 10,000</span><b className="text-amber-300">{pct.toFixed(1)}%</b></div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 via-pink-500 to-purple-500 transition-all" style={{ width: `${pct}%` }} data-testid="admin-refer-progress"></div></div>
      </div>

      {/* Top 10 referrers */}
      <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest opacity-70 font-black mb-2">🏆 Top 10 Referrers</div>
        {(data.top_referrers || []).length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-3">No signups yet — be the first to launch! 🚀</div>
        ) : (
          <div className="space-y-1 text-xs">
            {data.top_referrers.map((r, i) => (
              <div key={r.sponsor_id} className="flex items-center justify-between p-1.5 rounded bg-slate-800/50" data-testid={`admin-refer-top-${i}`}>
                <div className="flex items-center gap-2 min-w-0"><span className="text-amber-300 font-black w-5">#{i + 1}</span><span className="truncate"><b>{r.username}</b>{r.name && <span className="opacity-70"> · {r.name}</span>}</span></div>
                <div className="flex gap-3 text-[11px]"><span className="text-cyan-300 font-black">{r.count} refs</span><span className="text-emerald-300 font-black">{r.at_earned.toFixed(0)} AT</span></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ============ Anant Yojana Mitra · Schemes Admin Panel ============
function YojanaSchemesPanel() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, published: 0, inactive: 0 });
  const [filter, setFilter] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const CATEGORIES = ['Education', 'Agriculture', 'Healthcare', 'Livelihood & Employment', 'Housing', 'Social Welfare', 'Women & Child Development', 'Minority Affairs', 'Rural Development', 'MSME', 'Digital India', 'Environment & Energy', 'Finance & Banking'];
  const [form, setForm] = useState({ title: '', description: '', benefits: '', apply_link: '', tutorial_link: '', category: 'Education', states: 'All India', min_age: 0, max_age: 120, genders: 'Any', castes: 'Any', income_ranges: 'Any', professions: 'Any' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const r = await api.get('/admin/anant-yojana/schemes', { params });
      setRows(r.data?.schemes || []);
      setCounts(r.data?.counts || { draft: 0, published: 0, inactive: 0 });
    } catch { toast.error('Schemes load failed'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const publish = async (id) => {
    if (!window.confirm('Publish this scheme? It becomes visible to all subscribed users.')) return;
    try {
      await api.put(`/admin/anant-yojana/schemes/${id}/publish`);
      toast.success('Published ✓'); load();
    } catch { toast.error('Publish failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this scheme? Irreversible.')) return;
    try {
      await api.delete(`/admin/anant-yojana/schemes/${id}`);
      toast.success('Deleted'); load();
    } catch { toast.error('Delete failed'); }
  };
  const startEdit = (s) => {
    setEditId(s.id);
    setForm({
      title: s.title || '', description: s.description || '', benefits: s.benefits || '',
      apply_link: s.apply_link || '', tutorial_link: s.tutorial_link || '',
      category: s.category || 'Education',
      states: (s.states || ['All India']).join(', '),
      min_age: s.min_age ?? 0, max_age: s.max_age ?? 120,
      genders: (s.genders || ['Any']).join(', '),
      castes: (s.castes || ['Any']).join(', '),
      income_ranges: (s.income_ranges || ['Any']).join(', '),
      professions: (s.professions || ['Any']).join(', '),
    });
    setShowForm(true);
  };
  const resetForm = () => {
    setForm({ title: '', description: '', benefits: '', apply_link: '', tutorial_link: '', category: 'Education', states: 'All India', min_age: 0, max_age: 120, genders: 'Any', castes: 'Any', income_ranges: 'Any', professions: 'Any' });
    setEditId(null);
  };
  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.apply_link.trim()) { toast.error('Title, description, apply link required'); return; }
    const payload = {
      title: form.title.trim(), description: form.description.trim(), benefits: form.benefits.trim() || '—',
      apply_link: form.apply_link.trim(), tutorial_link: form.tutorial_link.trim() || null,
      category: form.category,
      states: form.states.split(',').map((x) => x.trim()).filter(Boolean),
      min_age: parseInt(form.min_age) || 0, max_age: parseInt(form.max_age) || 120,
      genders: form.genders.split(',').map((x) => x.trim()).filter(Boolean),
      castes: form.castes.split(',').map((x) => x.trim()).filter(Boolean),
      income_ranges: form.income_ranges.split(',').map((x) => x.trim()).filter(Boolean),
      professions: form.professions.split(',').map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (editId) {
        await api.put(`/admin/anant-yojana/schemes/${editId}`, payload);
        toast.success('Updated ✓');
      } else {
        await api.post('/admin/anant-yojana/schemes', payload);
        toast.success('Draft created ✓');
      }
      setShowForm(false); resetForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  // ===== Bulk CSV Import =====
  const [csvText, setCsvText] = useState('');
  const [autoPublish, setAutoPublish] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const onCsvFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('CSV too large (max 2 MB)'); return; }
    const text = await f.text();
    setCsvText(text);
    toast.success(`Loaded ${f.name}`);
  };
  const doImport = async () => {
    if (!csvText.trim()) { toast.error('Paste or upload CSV first'); return; }
    setImporting(true); setImportResult(null);
    try {
      const r = await api.post('/admin/anant-yojana/schemes/bulk-import', { csv_text: csvText, auto_publish: autoPublish });
      setImportResult(r.data);
      toast.success(`✅ Imported ${r.data.imported} schemes (skipped ${r.data.skipped})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };
  const CSV_TEMPLATE = 'title,description,benefits,apply_link,tutorial_link,category,states,min_age,max_age,genders,castes,income_ranges,professions\n"Mahatma Jyotiba Phule Jan Arogya Yojana","Maharashtra state health insurance up to ₹1.5 lakh per family per year.","₹1.5L health cover|Cashless treatment","https://www.jeevandayee.gov.in","https://youtube.com/results?search_query=MJPJAY","Healthcare","Maharashtra",0,120,"Any","Any","Below 1L|1L-3L","Any"\n"UP Kanya Sumangala Yojana","UP state scheme giving ₹15,000 in 6 installments to girl child from birth till graduation.","₹15,000 across life stages","https://mksy.up.gov.in","","Women & Child Development","Uttar Pradesh",0,25,"Female","Any","Below 1L|1L-3L","Any"';
  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'yojana_schemes_template.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-4 mb-4" data-testid="admin-yojana-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🗂️ Yojana Schemes · Approval &amp; Management</div>
          <div className="text-xs opacity-80">Draft {counts.draft} · Published {counts.published} · Inactive {counts.inactive}</div>
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="yj-admin-add" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs font-black">+ Add Scheme</button>
          <button onClick={() => setShowImport(true)} data-testid="yj-admin-bulk-import-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-xs font-black">📥 Bulk CSV Import</button>
          <button onClick={load} disabled={loading} className="px-2 py-1.5 rounded-lg bg-white/10 text-xs flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload</button>
        </div>
      </div>

      <div className="flex gap-1 flex-wrap mb-3">
        {['draft', 'published', 'inactive', 'all'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`yj-admin-filter-${f}`} className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase ${filter === f ? 'bg-emerald-500 text-black' : 'bg-white/5 text-slate-300 hover:bg-white/15'}`}>{f}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 max-h-[480px] overflow-y-auto pr-1">
        {rows.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-6">No schemes in this filter. Use + Add Scheme.</div>
        ) : rows.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-700 bg-slate-900/60 p-2.5 flex items-start justify-between gap-2 flex-wrap" data-testid={`yj-admin-scheme-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-black ${s.status === 'published' ? 'bg-emerald-500/25 text-emerald-200' : s.status === 'draft' ? 'bg-amber-500/25 text-amber-200' : 'bg-slate-700 text-slate-300'}`}>{s.status}</span>
                <span className="text-[9px] uppercase text-cyan-300 opacity-80">{s.category}</span>
              </div>
              <div className="text-sm font-black mt-0.5 leading-tight">{s.title}</div>
              <div className="text-[10px] opacity-70 line-clamp-2 mt-0.5">{s.description}</div>
              <div className="text-[9px] opacity-60 mt-1">Age {s.min_age}-{s.max_age} · {(s.states || []).slice(0, 3).join(', ')}{(s.states || []).length > 3 ? '…' : ''}</div>
            </div>
            <div className="flex flex-col gap-1">
              {s.status !== 'published' && <button onClick={() => publish(s.id)} data-testid={`yj-admin-publish-${s.id}`} className="text-[10px] px-2 py-1 rounded bg-emerald-500 text-black font-black">✓ Publish</button>}
              <button onClick={() => startEdit(s)} data-testid={`yj-admin-edit-${s.id}`} className="text-[10px] px-2 py-1 rounded bg-white/10 hover:bg-white/20">Edit</button>
              <button onClick={() => del(s.id)} data-testid={`yj-admin-delete-${s.id}`} className="text-[10px] px-2 py-1 rounded bg-red-500/25 text-red-300 hover:bg-red-500/40">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Bulk CSV Import modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => setShowImport(false)}>
          <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-cyan-400/50 bg-slate-900 p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="yj-admin-import-modal">
            <div className="flex items-center justify-between">
              <div className="text-sm font-black text-cyan-300">📥 Bulk CSV Import · Yojana Schemes</div>
              <button onClick={() => setShowImport(false)} className="text-lg opacity-70">✕</button>
            </div>
            <div className="text-[10px] opacity-80 leading-relaxed">
              Upload/paste a CSV with headers: <code className="text-cyan-200">title, description, benefits, apply_link, tutorial_link, category, states, min_age, max_age, genders, castes, income_ranges, professions</code>. Array columns use <b>|</b> or <b>;</b> as separator inside a cell (e.g. <code className="text-cyan-200">Maharashtra|Gujarat</code>). Missing required fields = row skipped.
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={downloadTemplate} data-testid="yj-admin-csv-template" className="px-2 py-1.5 rounded-lg bg-white/10 text-xs font-black">⬇️ Download CSV Template</button>
              <label className="px-2 py-1.5 rounded-lg bg-white/10 text-xs font-black cursor-pointer">
                📁 Choose CSV File
                <input type="file" accept=".csv,text/csv" onChange={onCsvFile} data-testid="yj-admin-csv-file" className="hidden" />
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer px-2 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-400/30">
                <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} data-testid="yj-admin-auto-publish" />
                Auto-publish (skip Draft)
              </label>
            </div>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="Paste CSV here… or use 'Choose CSV File' above"
              rows={10}
              data-testid="yj-admin-csv-textarea"
              className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-[11px] font-mono"
            />
            <div className="text-[10px] opacity-70">{csvText ? `${csvText.split('\n').length - 1} rows detected (excluding header)` : 'No CSV loaded'}</div>
            {importResult && (
              <div className={`rounded-lg p-2 text-[11px] ${importResult.imported > 0 ? 'bg-emerald-500/15 border border-emerald-400/40' : 'bg-red-500/15 border border-red-400/40'}`} data-testid="yj-admin-import-result">
                <div className="font-black">✅ Imported: {importResult.imported} · ⚠️ Skipped: {importResult.skipped} · Status: {importResult.status_applied}</div>
                {(importResult.errors || []).length > 0 && (
                  <div className="mt-1 opacity-80">
                    {importResult.errors.slice(0, 5).map((e, i) => <div key={i}>· {e}</div>)}
                    {importResult.errors.length > 5 && <div className="opacity-60">…and {importResult.errors.length - 5} more</div>}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setShowImport(false); setCsvText(''); setImportResult(null); }} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-white text-xs">Close</button>
              <button onClick={doImport} disabled={importing || !csvText.trim()} data-testid="yj-admin-import-submit" className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-xs font-black disabled:opacity-40">
                {importing ? 'Importing…' : `🚀 Import ${csvText ? csvText.split('\n').length - 1 : 0} rows`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-emerald-400/50 bg-slate-900 p-5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="yj-admin-form">
            <div className="text-sm font-black text-emerald-300">{editId ? '✏️ Edit Scheme' : '+ New Scheme'}</div>
            <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="yj-admin-form-title" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <textarea rows={3} placeholder="Description *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="yj-admin-form-desc" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <textarea rows={2} placeholder="Benefits" value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="Apply Link (official govt URL) *" value={form.apply_link} onChange={(e) => setForm({ ...form, apply_link: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <input placeholder="Tutorial Link (YouTube — optional)" value={form.tutorial_link} onChange={(e) => setForm({ ...form, tutorial_link: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input placeholder="States (comma-sep · use 'All India')" value={form.states} onChange={(e) => setForm({ ...form, states: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" placeholder="Min Age" value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" placeholder="Max Age" value={form.max_age} onChange={(e) => setForm({ ...form, max_age: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input placeholder="Genders (Any / Male, Female)" value={form.genders} onChange={(e) => setForm({ ...form, genders: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input placeholder="Castes (Any / General, OBC, SC…)" value={form.castes} onChange={(e) => setForm({ ...form, castes: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input placeholder="Income Ranges (Any / Below 1L…)" value={form.income_ranges} onChange={(e) => setForm({ ...form, income_ranges: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input placeholder="Professions (Any / Farmer, Student…)" value={form.professions} onChange={(e) => setForm({ ...form, professions: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowForm(false)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-white text-xs">Cancel</button>
              <button onClick={submit} data-testid="yj-admin-form-submit" className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs font-black">{editId ? 'Update' : 'Save Draft'}</button>
            </div>
            <div className="text-[9px] opacity-60">💡 Comma-separated arrays for eligibility. Use &quot;Any&quot; to match all. Draft schemes are hidden until you Publish.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CSC Digital Services Admin Panel ============
function CSCServicesPanel() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ service_name: '', category: 'Identity Proof', description: '', official_link: '', training_video_url: '', image_icon_url: '', status: 'active' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/anant-yojana/csc/services');
      setRows(r.data?.services || []); setCats(r.data?.categories || []);
    } catch { toast.error('CSC load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (s) => {
    setEditId(s.id);
    setForm({
      service_name: s.service_name || '', category: s.category || 'Identity Proof',
      description: s.description || '', official_link: s.official_link || '',
      training_video_url: s.training_video_url || '', image_icon_url: s.image_icon_url || '',
      status: s.status || 'active',
    });
    setShowForm(true);
  };
  const reset = () => { setForm({ service_name: '', category: cats[0] || 'Identity Proof', description: '', official_link: '', training_video_url: '', image_icon_url: '', status: 'active' }); setEditId(null); };
  const submit = async () => {
    if (!form.service_name.trim() || !form.official_link.trim()) { toast.error('Name + official link required'); return; }
    try {
      if (editId) await api.put(`/admin/anant-yojana/csc/services/${editId}`, form);
      else await api.post('/admin/anant-yojana/csc/services', form);
      toast.success(editId ? 'Updated' : 'Added');
      setShowForm(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try { await api.delete(`/admin/anant-yojana/csc/services/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="rounded-2xl border-2 border-cyan-400/40 bg-cyan-500/5 p-4 mb-4" data-testid="admin-csc-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🌐 CSC Digital Services · Netflix Catalog</div>
          <div className="text-xs opacity-80">{rows.length} services · {rows.filter((r) => r.status === 'active').length} active</div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => { reset(); setShowForm(true); }} data-testid="csc-admin-add" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-xs font-black">+ Add Service</button>
          <button onClick={load} disabled={loading} className="px-2 py-1.5 rounded-lg bg-white/10 text-xs flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-1">
        {rows.length === 0 ? (
          <div className="col-span-2 text-center text-xs text-slate-400 py-6">No services. Add first one.</div>
        ) : rows.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-700 bg-slate-900/60 p-2.5" data-testid={`csc-admin-${s.id}`}>
            <div className="flex items-center gap-2">
              {s.image_icon_url && <img src={s.image_icon_url} alt="" className="w-8 h-8 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase text-cyan-300 opacity-80">{s.category}</div>
                <div className="text-xs font-black leading-tight truncate">{s.service_name}</div>
              </div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${s.status === 'active' ? 'bg-emerald-500/25 text-emerald-200' : 'bg-slate-700 text-slate-300'}`}>{s.status}</span>
            </div>
            <div className="flex gap-1 mt-2">
              <button onClick={() => startEdit(s)} className="text-[10px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Edit</button>
              <button onClick={() => del(s.id)} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300 hover:bg-red-500/40">Delete</button>
            </div>
          </div>
        ))}
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-cyan-400/50 bg-slate-900 p-5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="csc-admin-form">
            <div className="text-sm font-black text-cyan-300">{editId ? 'Edit Service' : '+ New Digital Service'}</div>
            <input placeholder="Service Name *" value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} data-testid="csc-form-name" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              {cats.map((c) => <option key={c}>{c}</option>)}
            </select>
            <textarea rows={2} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="Official Government Link *" value={form.official_link} onChange={(e) => setForm({ ...form, official_link: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <input placeholder="Training Video Embed URL (YouTube /embed/ or link)" value={form.training_video_url} onChange={(e) => setForm({ ...form, training_video_url: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <input placeholder="Icon Image URL (optional)" value={form.image_icon_url} onChange={(e) => setForm({ ...form, image_icon_url: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value="active">Active (visible to users)</option>
              <option value="inactive">Inactive (hidden)</option>
            </select>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowForm(false)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-white text-xs">Cancel</button>
              <button onClick={submit} data-testid="csc-form-submit" className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-xs font-black">{editId ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ============ CSC Google Sheet Auto-Sync Panel ============
function CSCSheetSyncPanel() {
  const [cfg, setCfg] = useState({ csv_url: '', last_synced_at: null, last_result: null });
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/anant-yojana/csc/sheet-config');
      setCfg(r.data); setUrl(r.data.csv_url || '');
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!url.trim()) { toast.error('CSV URL required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/anant-yojana/csc/sheet-config', { csv_url: url.trim() });
      toast.success('Sheet URL saved');
      await load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  const syncNow = async () => {
    setSyncing(true); setResult(null);
    try {
      const r = await api.post('/admin/anant-yojana/csc/sync-from-sheet');
      setResult(r.data);
      toast.success(`Synced! +${r.data.inserted} inserted, ↻${r.data.updated} updated`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-blue-400/40 bg-blue-500/5 p-4 mb-4" data-testid="admin-csc-sync-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-blue-300 font-black">📊 CSC Google Sheet · Auto-Upload Logic</div>
          <div className="text-xs opacity-80">Publish sheet as CSV, paste URL, sync anytime. Unique by service_name (upsert).</div>
        </div>
        <button onClick={syncNow} disabled={syncing || !cfg.csv_url} data-testid="csc-sync-now" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-black disabled:opacity-40 flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : '↻ Sync Now'}
        </button>
      </div>
      <div className="text-[10px] opacity-70 mb-2">
        Sheet columns (in order): <b>category, service_name, image_icon_url, official_link, training_video_url, status</b>. Publish → File → Share → Publish to web → CSV.
      </div>
      <div className="flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…/pub?output=csv" data-testid="csc-sheet-url" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
        <button onClick={save} disabled={saving} data-testid="csc-sheet-save" className="px-3 py-1.5 rounded bg-slate-700 text-xs font-black">{saving ? 'Saving…' : 'Save URL'}</button>
      </div>
      {cfg.last_synced_at && (
        <div className="text-[10px] mt-2 opacity-70">Last synced: {new Date(cfg.last_synced_at).toLocaleString()}
          {cfg.last_result && <> · +{cfg.last_result.inserted} inserted · ↻{cfg.last_result.updated} updated · {cfg.last_result.skipped || 0} skipped</>}
        </div>
      )}
      {result && (
        <div className="mt-2 rounded bg-slate-800/60 p-2 text-[11px]">
          <div className="font-black text-blue-300">Result</div>
          <div>Inserted: {result.inserted} · Updated: {result.updated} · Skipped: {result.skipped}</div>
          {result.errors?.length > 0 && <ul className="mt-1 opacity-70 list-disc pl-4">{result.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

// ============ Anant Khata Admin Panel ============
function KhataAdminPanel() {
  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ key: '', label: '', columns: [{ key: 'amount', label: 'Amount ₹', type: 'number' }] });

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get('/admin/anant-khata/subscriptions'),
        api.get('/admin/anant-khata/categories'),
      ]);
      setSubs(s.data?.subscriptions || []); setCats(c.data?.categories || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm({ key: '', label: '', columns: [{ key: 'amount', label: 'Amount ₹', type: 'number' }] }); setEditId(null); };
  const startEdit = (c) => { setEditId(c.id); setForm({ key: c.key, label: c.label, columns: c.columns || [] }); setShowForm(true); };
  const submit = async () => {
    if (!form.key.trim() || !form.label.trim()) { toast.error('Key + Label required'); return; }
    try {
      if (editId) await api.put(`/admin/anant-khata/categories/${editId}`, form);
      else await api.post('/admin/anant-khata/categories', form);
      toast.success(editId ? 'Updated' : 'Added');
      setShowForm(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (c) => {
    if (c.system) { toast.error('System category cannot be deleted'); return; }
    if (!window.confirm(`Delete category "${c.label}"?`)) return;
    try { await api.delete(`/admin/anant-khata/categories/${c.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };
  const addColumn = () => setForm({ ...form, columns: [...form.columns, { key: '', label: '', type: 'text' }] });
  const rmColumn = (i) => setForm({ ...form, columns: form.columns.filter((_, x) => x !== i) });
  const updColumn = (i, k, v) => setForm({ ...form, columns: form.columns.map((c, x) => x === i ? { ...c, [k]: v } : c) });

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border-2 border-amber-400/40 bg-amber-500/5 p-4" data-testid="admin-khata-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🗂️ Vendor Categories · Dynamic Columns</div>
            <div className="text-xs opacity-80">{cats.length} categories · Admin can add new business types with custom columns</div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => { reset(); setShowForm(true); }} data-testid="khata-cat-add" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs font-black">+ Add Category</button>
            <button onClick={load} className="px-2 py-1.5 rounded-lg bg-white/10 text-xs flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reload</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
          {cats.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-700 bg-slate-900/60 p-2.5" data-testid={`khata-cat-${c.key}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-black">{c.label} <span className="text-[9px] opacity-60 font-mono">({c.key})</span> {c.system && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-300 ml-1">SYSTEM</span>}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">{(c.columns || []).map((x) => x.label).join(' · ') || 'no columns'}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(c)} className="text-[10px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Edit</button>
                  {!c.system && <button onClick={() => del(c)} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300 hover:bg-red-500/40">Del</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="text-[10px] uppercase tracking-widest text-orange-300 font-black mb-2">📅 Recent Subscriptions ({subs.length})</div>
        <div className="max-h-64 overflow-y-auto text-xs">
          <table className="w-full">
            <thead className="text-left text-[10px] opacity-70 border-b border-slate-700"><tr><th>User</th><th>Plan</th><th>Amount</th><th>Referral</th><th>Status</th><th>Expires</th></tr></thead>
            <tbody>
              {subs.slice(0, 100).map((s) => (
                <tr key={s.id} className="border-b border-slate-800">
                  <td className="py-1">{s.username || '—'}</td>
                  <td>{s.plan_label}</td>
                  <td>{s.amount} AT</td>
                  <td className="opacity-70">{s.referral_used ? `-${s.referral_used}` : '—'}</td>
                  <td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-500/30' : 'bg-slate-700'}`}>{s.status}</span></td>
                  <td>{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {subs.length === 0 && <div className="text-center py-4 opacity-60">No subscriptions yet.</div>}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-amber-400/50 bg-slate-900 p-5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="khata-cat-form">
            <div className="text-sm font-black text-amber-300">{editId ? 'Edit Category' : '+ New Vendor Category'}</div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Key (e.g. saloon)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })} disabled={!!editId} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
              <input placeholder="Label (e.g. Saloon / Salon)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 mt-2">Dynamic Columns</div>
            {form.columns.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_80px_28px] gap-1">
                <input placeholder="key" value={c.key} onChange={(e) => updColumn(i, 'key', e.target.value)} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
                <input placeholder="label" value={c.label} onChange={(e) => updColumn(i, 'label', e.target.value)} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
                <select value={c.type} onChange={(e) => updColumn(i, 'type', e.target.value)} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="text">text</option><option value="number">number</option><option value="date">date</option><option value="checkbox">checkbox</option>
                </select>
                <button onClick={() => rmColumn(i)} className="px-2 py-1 rounded bg-red-500/25 text-red-300 text-xs">×</button>
              </div>
            ))}
            <button onClick={addColumn} className="w-full px-2 py-1 rounded bg-slate-700 text-xs">+ Add Column</button>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowForm(false)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-xs">Cancel</button>
              <button onClick={submit} data-testid="khata-cat-submit" className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs font-black">{editId ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ Corpus50 Certificate Theme Panel ============
const CORPUS50_THEMES = [
  { key: 'royal_blue_gold',   label: 'Royal Blue & Gold',   emoji: '🏛️', ribbon: 'linear-gradient(90deg,#1e3a8a,#c8a24a,#1e3a8a)' },
  { key: 'emerald_regal',     label: 'Emerald Regal',       emoji: '🌿', ribbon: 'linear-gradient(90deg,#065f46,#d4a017,#065f46)' },
  { key: 'crimson_gold',      label: 'Crimson Gold',        emoji: '❤️', ribbon: 'linear-gradient(90deg,#7f1d1d,#eab308,#7f1d1d)' },
  { key: 'midnight_platinum', label: 'Midnight Platinum',   emoji: '🌌', ribbon: 'linear-gradient(90deg,#0f172a,#94a3b8,#0f172a)' },
  { key: 'saffron_maroon',    label: 'Saffron & Maroon',    emoji: '🕉️', ribbon: 'linear-gradient(90deg,#7c2d12,#f59e0b,#7c2d12)' },
];

function Corpus50CertThemePanel() {
  const [current, setCurrent] = useState('royal_blue_gold');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/corpus50/cert-theme');
        setCurrent(r.data?.theme || 'royal_blue_gold');
      } catch { /* silent */ }
    })();
  }, []);

  const save = async (k) => {
    setSaving(true);
    try {
      await api.post('/admin/corpus50/cert-theme', { theme: k });
      setCurrent(k);
      toast.success(`Default theme set: ${k}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-orange-950/40 p-4 mb-4" data-testid="admin-corpus50-theme-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🎨 Certificate Theme · Global Default</div>
          <div className="text-xs opacity-80">Users can pick their own theme, but new bonds/first-view use this default. Current: <b className="text-amber-300">{current}</b></div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {CORPUS50_THEMES.map((t) => (
          <button
            key={t.key}
            onClick={() => save(t.key)}
            disabled={saving}
            data-testid={`admin-cert-theme-${t.key}`}
            className={`rounded-xl border-2 p-2 text-left transition-all ${current === t.key ? 'border-amber-300 bg-amber-500/20 scale-[1.02] shadow-lg' : 'border-slate-700 bg-slate-900/60 hover:border-amber-400/50'}`}>
            <div className="h-8 rounded" style={{ background: t.ribbon }}></div>
            <div className="text-[11px] font-black mt-1">{t.emoji} {t.label}</div>
            {current === t.key && <div className="text-[9px] text-amber-300 font-black mt-0.5">✓ ACTIVE DEFAULT</div>}
          </button>
        ))}
      </div>
    </div>
  );
}


// ============ Land Records Admin Panel (Anant 7/12 & Property Portal) ============
function LandRecordsAdminPanel() {
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({ status: '', state: '' });
  const [configs, setConfigs] = useState([]);
  const [editing, setEditing] = useState(null);  // state row being edited
  const [form, setForm] = useState({ state: 'MH', enabled: false, url: '', api_key: '', auth_header: 'Authorization', auth_prefix: 'Bearer', provider: 'mahabhulekh', cost_per_report_inr: 8, timeout_sec: 45 });

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (filter.status) q.set('status', filter.status);
      if (filter.state) q.set('state', filter.state);
      const [l, c] = await Promise.all([
        api.get(`/admin/anant-yojana/land-records/logs?${q.toString()}`),
        api.get('/admin/anant-yojana/land-records/api-configs'),
      ]);
      setOrders(l.data?.orders || []);
      setStats(l.data?.stats || {});
      setConfigs(c.data?.configs || []);
    } catch { /* silent */ }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const forceRefund = async (o) => {
    if (!window.confirm(`Refund ${o.at_deducted} AT to @${o.username} for order ${o.order_id}?`)) return;
    try {
      await api.post(`/admin/anant-yojana/land-records/${o.order_id}/refund`);
      toast.success('Refunded');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Refund failed'); }
  };

  const startEdit = (c) => {
    setEditing(c.state);
    setForm({
      state: c.state, enabled: c.enabled, url: c.url || '',
      api_key: '', auth_header: 'Authorization', auth_prefix: 'Bearer',
      provider: c.provider || 'mahabhulekh',
      cost_per_report_inr: c.cost_per_report_inr || 8, timeout_sec: c.timeout_sec || 45,
    });
  };

  const saveConfig = async () => {
    try {
      await api.post('/admin/anant-yojana/land-records/api-configs', form);
      toast.success(`${form.state} config saved${form.enabled ? ' — LIVE' : ' — DISABLED'}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const statusColor = {
    PROCESSING: 'bg-amber-500/25 text-amber-300',
    COMPLETED: 'bg-emerald-500/25 text-emerald-300',
    REFUNDED: 'bg-red-500/25 text-red-300',
    FAILED: 'bg-red-500/25 text-red-300',
    PENDING: 'bg-slate-500/25 text-slate-300',
  };

  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-orange-950/40 p-4 mb-4" data-testid="admin-land-panel">
      <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-3 flex items-center gap-1">🏡 Anant 7/12 & Property Portal · Orders & B2B APIs</div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <StatCard label="Total Orders" value={stats.total || 0} color="slate" />
        <StatCard label="Completed" value={stats.completed || 0} color="emerald" />
        <StatCard label="Processing" value={stats.processing || 0} color="amber" />
        <StatCard label="Refunded" value={stats.refunded || 0} color="red" />
        <StatCard label={`Revenue · Net ₹${Math.round((stats.revenue_at || 0) - (stats.api_cost_inr || 0))}`} value={`${(stats.revenue_at || 0).toLocaleString()} AT`} color="amber" />
      </div>

      {/* B2B API configs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-blue-300 font-black mb-2">🔌 B2B Land API Configs (per state)</div>
        <div className="text-[10px] opacity-70 mb-2">Configure MahaBhulekh / AnyROR / custom provider URL + API key. Enabled state auto-switches worker to real API (else uses simulation). Failure ke baad AT auto-refund, LLM key never touched on failure.</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {configs.map((c) => (
            <div key={c.state} className={`rounded-lg border p-2 ${c.enabled ? 'border-emerald-400/50 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/40'}`} data-testid={`land-api-cfg-${c.state}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-black">{c.state_label} <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ml-1 ${c.enabled ? 'bg-emerald-500 text-black' : 'bg-slate-700 text-slate-300'}`}>{c.enabled ? 'LIVE' : 'OFF'}</span></div>
                <button onClick={() => startEdit(c)} data-testid={`land-api-edit-${c.state}`} className="text-[10px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 font-black">Edit</button>
              </div>
              <div className="text-[9px] opacity-70 truncate">{c.url || <em>not configured</em>}</div>
              <div className="text-[9px] opacity-60">Provider: {c.provider || '—'} · Cost ₹{c.cost_per_report_inr}/report · {c.has_key ? '🔐 Key set' : '❌ No key'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-2 flex-wrap">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="land-filter-status" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">All Statuses</option>
          {['PROCESSING', 'COMPLETED', 'REFUNDED', 'FAILED'].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filter.state} onChange={(e) => setFilter({ ...filter, state: e.target.value })} data-testid="land-filter-state" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">All States</option>
          {configs.map((c) => <option key={c.state} value={c.state}>{c.state_label}</option>)}
        </select>
        <button onClick={load} className="text-xs px-2 py-1 rounded bg-white/10 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reload</button>
      </div>

      {/* Orders table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900/60 max-h-80 overflow-y-auto" data-testid="land-orders-table">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-950 border-b border-slate-700 text-left">
            <tr>
              <th className="p-2">Order</th><th>User</th><th>Service</th><th>Location</th><th>Status</th><th>API</th><th>Cost₹</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`land-row-${o.order_id}`}>
                <td className="p-2"><div className="font-mono text-[10px]">{o.order_id}</div><div className="text-[9px] opacity-60">{new Date(o.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div></td>
                <td>@{o.username || '—'}</td>
                <td className="text-[10px]">{o.service_type}</td>
                <td className="text-[10px]">{o.village}, {o.taluka}, {o.state}</td>
                <td><span className={`inline-block px-2 py-0.5 rounded text-[9px] font-black ${statusColor[o.status] || statusColor.PENDING}`}>{o.status}</span></td>
                <td className="text-[10px]">{o.api_used || '—'}</td>
                <td className="text-[10px]">{o.api_cost_inr || 0}</td>
                <td>
                  {o.status !== 'REFUNDED' && <button onClick={() => forceRefund(o)} data-testid={`land-refund-${o.order_id}`} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300 font-black hover:bg-red-500/40">Refund</button>}
                  {o.ai_summary && <span title={o.ai_summary} className="ml-1 text-[9px] opacity-60 cursor-help">💬</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && <div className="p-6 text-center text-xs opacity-60">No orders match the filters</div>}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/85 z-[130] flex items-center justify-center p-3" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-2xl border-2 border-blue-400/50 bg-slate-900 p-5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="land-api-form">
            <div className="text-sm font-black text-blue-300">🔌 B2B API Config · {form.state}</div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="land-api-enabled" />
              <span className="font-black">{form.enabled ? '✅ ENABLED — Real API will be called' : '⚠️ Disabled — Fallback to 92% simulation'}</span>
            </label>
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value="mahabhulekh">MahaBhulekh (Maharashtra Bhulekh)</option>
              <option value="anyror">AnyROR (Gujarat)</option>
              <option value="mp_bhulekh">MP Bhulekh</option>
              <option value="up_bhulekh">UP Bhulekh</option>
              <option value="custom">Custom Provider</option>
            </select>
            <input placeholder="POST endpoint URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="land-api-url" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <input type="password" placeholder="API Key (leave blank to keep existing)" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="land-api-key" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Auth Header (e.g. Authorization)" value={form.auth_header} onChange={(e) => setForm({ ...form, auth_header: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input placeholder="Prefix (Bearer / Token / '')" value={form.auth_prefix} onChange={(e) => setForm({ ...form, auth_prefix: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" placeholder="Cost/report ₹" value={form.cost_per_report_inr} onChange={(e) => setForm({ ...form, cost_per_report_inr: parseFloat(e.target.value) || 0 })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" placeholder="Timeout sec" value={form.timeout_sec} onChange={(e) => setForm({ ...form, timeout_sec: parseInt(e.target.value) || 45 })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-xs">Cancel</button>
              <button onClick={saveConfig} data-testid="land-api-save" className="flex-1 px-3 py-2 rounded-lg bg-blue-500 text-white text-xs font-black">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  const map = { emerald: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10', amber: 'text-amber-300 border-amber-400/30 bg-amber-500/10', red: 'text-red-300 border-red-400/30 bg-red-500/10', slate: 'text-slate-300 border-slate-600 bg-slate-800/50' };
  return (
    <div className={`rounded-lg border p-2 ${map[color] || map.slate}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}


// ============ ASN Admin Panel ============
function ASNAdminPanel() {
  const [stats, setStats] = useState({});
  const [reporters, setReporters] = useState([]);
  const [content, setContent] = useState([]);
  const [ads, setAds] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [tickerForm, setTickerForm] = useState({ text: '', city: '', color: 'red', expires_hours: 24 });
  const [directForm, setDirectForm] = useState({ content_type: 'POST', title: '', body: '', media_url: '', youtube_url: '', city: '', category: 'General', accept_disclaimer: true });
  const [kycFilter, setKycFilter] = useState('PENDING');
  const [settings, setSettings] = useState({ trust_score_threshold: 5 });
  const [settingsInput, setSettingsInput] = useState(5);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [helplines, setHelplines] = useState([]);
  const [helplineForm, setHelplineForm] = useState({ city: '', category: '', name: '', number: '', icon: '📞' });
  const [polls, setPolls] = useState([]);
  const [pollForm, setPollForm] = useState({ question: '', options: ['', ''], city: '', expires_hours: 72 });
  const [contentAll, setContentAll] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, r, c, a, t, cfg, feat, cats, hl, pl, cAll] = await Promise.all([
        api.get('/admin/asn/stats'),
        api.get('/admin/asn/reporters', { params: kycFilter ? { kyc: kycFilter } : {} }),
        api.get('/admin/asn/content', { params: { status: 'PENDING' } }),
        api.get('/admin/asn/ads', { params: { status: 'PENDING' } }),
        api.get('/admin/asn/tickers'),
        api.get('/admin/asn/settings'),
        api.get('/admin/asn/content', { params: { status: 'APPROVED', limit: 100 } }),
        api.get('/admin/asn/categories'),
        api.get('/admin/asn/helplines'),
        api.get('/admin/asn/polls'),
        api.get('/admin/asn/content/all', { params: { status: 'APPROVED', limit: 30 } }),
      ]);
      setStats(s.data?.stats || {});
      setReporters(r.data?.reporters || []);
      setContent(c.data?.content || []);
      setAds(a.data?.ads || []);
      setTickers(t.data?.tickers || []);
      const st = cfg.data?.settings || { trust_score_threshold: 5 };
      setSettings(st);
      setSettingsInput(st.trust_score_threshold || 5);
      setFeaturedVideos((feat.data?.content || []).filter((x) => x.content_type === 'VIDEO' && x.youtube_url));
      setCategories(cats.data?.categories || []);
      setHelplines(hl.data?.helplines || []);
      setPolls(pl.data?.polls || []);
      setContentAll(cAll.data?.content || []);
    } catch { /* silent */ }
  }, [kycFilter]);
  useEffect(() => { load(); }, [load]);

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try { await api.post('/admin/asn/categories', { name: newCategory.trim() }); toast.success('Category added'); setNewCategory(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delCategory = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/admin/asn/categories/${c.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const addHelpline = async () => {
    if (!helplineForm.category || !helplineForm.name || !helplineForm.number) { toast.error('Category + Name + Number required'); return; }
    try { await api.post('/admin/asn/helplines', helplineForm); toast.success('Helpline added'); setHelplineForm({ city: '', category: '', name: '', number: '', icon: '📞' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delHelpline = async (h) => {
    if (!window.confirm(`Delete ${h.name}?`)) return;
    try { await api.delete(`/admin/asn/helplines/${h.id}`); load(); } catch { toast.error('Failed'); }
  };
  const createPoll = async () => {
    const opts = pollForm.options.filter((o) => o.trim());
    if (!pollForm.question.trim() || opts.length < 2) { toast.error('Question + at least 2 options'); return; }
    try { await api.post('/admin/asn/polls', { ...pollForm, options: opts }); toast.success('Poll live'); setPollForm({ question: '', options: ['', ''], city: '', expires_hours: 72 }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delPoll = async (p) => {
    if (!window.confirm(`Delete poll "${p.question}"?`)) return;
    try { await api.delete(`/admin/asn/polls/${p.id}`); load(); } catch { toast.error('Failed'); }
  };
  const adjustExpiry = async (item, isAd = false) => {
    const val = window.prompt(`Extend by how many days? (negative = reduce)`, '7');
    if (val === null) return;
    const days = parseFloat(val);
    if (isNaN(days)) return toast.error('Invalid days');
    try {
      const url = isAd ? `/admin/asn/ads/${item.id}/expiry` : `/admin/asn/content/${item.id}/expiry`;
      await api.post(url, { action: 'extend', days });
      toast.success(`Expiry ${days > 0 ? 'extended' : 'reduced'} by ${Math.abs(days)}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const togglePin = async (item) => {
    try {
      if (item.is_pinned) {
        await api.delete(`/admin/asn/content/${item.id}/pin`);
        toast.success('Unpinned');
      } else {
        const hrs = parseInt(window.prompt('Pin for how many hours? (default 24)', '24')) || 24;
        await api.post(`/admin/asn/content/${item.id}/pin`, { hours: hrs });
        toast.success(`📌 Pinned for ${hrs}h`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const saveSettings = async () => {
    try {
      const r = await api.post('/admin/asn/settings', { trust_score_threshold: parseInt(settingsInput) || 5 });
      setSettings(r.data?.settings || settings);
      toast.success(`Trust threshold set to ${r.data?.settings?.trust_score_threshold}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleFeature = async (v) => {
    try {
      const r = await api.post(`/admin/asn/content/${v.id}/feature`, { is_featured: !v.is_featured });
      toast.success(r.data?.is_featured ? '⭐ Featured on Live TV' : 'Unfeatured');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const kycAction = async (u, action, notes = '') => {
    try {
      await api.post(`/admin/asn/reporters/${u.user_id}/kyc`, { action, notes });
      toast.success(`KYC ${action}d for @${u.username}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const banReporter = async (u) => {
    const reason = window.prompt(`Ban @${u.username}? Reason:`, '');
    if (reason === null) return;
    try { await api.post(`/admin/asn/reporters/${u.user_id}/ban`, { banned: true, reason }); toast.success('Banned'); load(); }
    catch { toast.error('Failed'); }
  };
  const moderateContent = async (c, action) => {
    try { await api.post(`/admin/asn/content/${c.id}/moderate`, { action, admin_notes: '' }); toast.success(action); load(); }
    catch { toast.error('Failed'); }
  };
  const moderateAd = async (a, action) => {
    try { await api.post(`/admin/asn/ads/${a.id}/moderate`, { action, admin_notes: '' }); toast.success(action); load(); }
    catch { toast.error('Failed'); }
  };
  const pushTicker = async () => {
    if (!tickerForm.text.trim()) { toast.error('Text zaroori'); return; }
    try { await api.post('/admin/asn/tickers', tickerForm); toast.success('Ticker live'); setTickerForm({ ...tickerForm, text: '' }); load(); }
    catch { toast.error('Failed'); }
  };
  const delTicker = async (t) => {
    if (!window.confirm('Delete ticker?')) return;
    try { await api.delete(`/admin/asn/tickers/${t.id}`); load(); } catch { toast.error('Failed'); }
  };
  const directPublish = async () => {
    if (!directForm.title.trim() || !directForm.city.trim()) { toast.error('Title + City required'); return; }
    try { await api.post('/admin/asn/content/direct-publish', directForm); toast.success('Published directly'); setDirectForm({ ...directForm, title: '', body: '', media_url: '', youtube_url: '' }); }
    catch { toast.error('Failed'); }
  };

  const kycColor = { PENDING: 'bg-amber-500/25 text-amber-300', APPROVED: 'bg-emerald-500/25 text-emerald-300', REJECTED: 'bg-red-500/25 text-red-300', NOT_SUBMITTED: 'bg-slate-500/25 text-slate-300' };

  return (
    <div className="space-y-4" data-testid="admin-asn-panel">
      {/* Stats */}
      <div className="rounded-2xl border-2 border-blue-400/40 bg-gradient-to-br from-blue-950/40 to-slate-900 p-4">
        <div className="text-xs uppercase tracking-widest text-blue-300 font-black mb-3">📰 Anant Samachar Network · Admin Console</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Reporters" value={stats.reporters_total || 0} color="slate" />
          <StatCard label="KYC Pending" value={stats.kyc_pending || 0} color="amber" />
          <StatCard label="Active Subs" value={stats.active_subs || 0} color="emerald" />
          <StatCard label={`Admin Wallet · ${(stats.admin_wallet_at || 0).toLocaleString()} AT`} value={`+${(stats.content_pending || 0) + (stats.ads_pending || 0)} pending`} color="blue" />
        </div>
      </div>

      {/* Settings — Trust Score Threshold */}
      <div className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">⚙️ ASN Settings</div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] opacity-80 block mb-1">Trust Score Threshold for Auto-Publish</label>
            <input
              type="number" min={1} max={100} value={settingsInput}
              onChange={(e) => setSettingsInput(e.target.value)}
              data-testid="asn-trust-threshold-input"
              className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs"
            />
            <div className="text-[10px] opacity-70 mt-1">Reporters with trust_score ≥ this value auto-publish content (no admin review). Current: <b className="text-amber-300">{settings.trust_score_threshold}</b></div>
          </div>
          <button onClick={saveSettings} data-testid="asn-trust-threshold-save" className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-black">💾 Save</button>
        </div>
      </div>

      {/* Live TV Curation */}
      <div className="rounded-2xl border border-red-400/30 bg-red-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2">📺 Live TV Curation — Featured Videos ({featuredVideos.filter((v) => v.is_featured).length}/{featuredVideos.length})</div>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {featuredVideos.map((v) => (
            <div key={v.id} className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/50 p-2" data-testid={`asn-livetv-${v.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate flex items-center gap-1">{v.is_featured && <span className="text-amber-300">⭐</span>} {v.title}</div>
                <div className="text-[10px] opacity-70 truncate">{v.city} · @{v.username} · {new Date(v.created_at).toLocaleDateString('en-IN')}</div>
              </div>
              <button
                onClick={() => toggleFeature(v)}
                data-testid={`asn-feature-toggle-${v.id}`}
                className={`text-[10px] px-2 py-1 rounded font-black ${v.is_featured ? 'bg-amber-500 text-black' : 'bg-slate-700 text-slate-200'}`}
              >
                {v.is_featured ? '⭐ Featured' : '☆ Feature'}
              </button>
            </div>
          ))}
          {featuredVideos.length === 0 && <div className="text-xs opacity-60 text-center py-4">No approved videos yet</div>}
        </div>
      </div>

      {/* Categories CRUD */}
      <div className="rounded-2xl border border-purple-400/30 bg-purple-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-purple-300 font-black mb-2">🏷️ Categories ({categories.length})</div>
        <div className="flex gap-2 mb-2">
          <input placeholder="New category name" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="asn-cat-input" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={addCategory} data-testid="asn-cat-add" className="px-3 py-1.5 rounded bg-purple-500 text-white text-xs font-black">+ Add</button>
        </div>
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {categories.map((c) => (
            <span key={c.id} className={`text-[10px] px-2 py-1 rounded-full border flex items-center gap-1 ${c.system ? 'border-slate-600 bg-slate-800 text-slate-300' : 'border-purple-400 bg-purple-500/20 text-purple-200'}`}>
              {c.name} {c.system && <span className="text-[8px] opacity-70">·SYS</span>}
              {!c.system && <button onClick={() => delCategory(c)} data-testid={`asn-cat-del-${c.id}`} className="text-red-300 text-[10px] hover:text-red-500">✕</button>}
            </span>
          ))}
        </div>
      </div>

      {/* Helplines CRUD */}
      <div className="rounded-2xl border border-red-400/30 bg-red-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2">📞 Emergency Helplines ({helplines.length})</div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-1.5 mb-2">
          <input placeholder="City (blank=global)" value={helplineForm.city} onChange={(e) => setHelplineForm({ ...helplineForm, city: e.target.value })} data-testid="hl-city" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input placeholder="Category" value={helplineForm.category} onChange={(e) => setHelplineForm({ ...helplineForm, category: e.target.value })} data-testid="hl-cat" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input placeholder="Name" value={helplineForm.name} onChange={(e) => setHelplineForm({ ...helplineForm, name: e.target.value })} data-testid="hl-name" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input placeholder="Number" value={helplineForm.number} onChange={(e) => setHelplineForm({ ...helplineForm, number: e.target.value })} data-testid="hl-num" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input placeholder="Icon 📞" value={helplineForm.icon} onChange={(e) => setHelplineForm({ ...helplineForm, icon: e.target.value })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <button onClick={addHelpline} data-testid="hl-add" className="px-2 py-1 rounded bg-red-500 text-white text-[11px] font-black">+ Add</button>
        </div>
        <div className="max-h-52 overflow-y-auto space-y-1">
          {helplines.map((h) => (
            <div key={h.id} className="flex items-center gap-2 rounded bg-slate-800/60 p-1.5 text-[11px]" data-testid={`hl-row-${h.id}`}>
              <span className="text-lg">{h.icon}</span>
              <div className="flex-1 min-w-0"><div className="font-black truncate">{h.name} · <span className="font-mono text-red-300">{h.number}</span></div><div className="opacity-70 text-[10px]">{h.category} · {h.city || 'GLOBAL'}</div></div>
              <button onClick={() => delHelpline(h)} className="text-red-300 hover:text-red-500 text-xs">✕</button>
            </div>
          ))}
          {helplines.length === 0 && <div className="text-xs opacity-60 text-center py-4">No custom helplines · default 8 helplines auto-used</div>}
        </div>
      </div>

      {/* Polls CRUD */}
      <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">🗳️ Janata Ka Poll ({polls.length})</div>
        <div className="space-y-2 mb-2">
          <input placeholder="Poll Question" value={pollForm.question} onChange={(e) => setPollForm({ ...pollForm, question: e.target.value })} data-testid="poll-q" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          {pollForm.options.map((o, i) => (
            <div key={i} className="flex gap-1">
              <input placeholder={`Option ${i + 1}`} value={o} onChange={(e) => { const opts = [...pollForm.options]; opts[i] = e.target.value; setPollForm({ ...pollForm, options: opts }); }} data-testid={`poll-opt-${i}`} className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              {pollForm.options.length > 2 && <button onClick={() => setPollForm({ ...pollForm, options: pollForm.options.filter((_, j) => j !== i) })} className="px-2 rounded bg-red-500/25 text-red-300 text-xs">✕</button>}
            </div>
          ))}
          <div className="flex gap-1">
            {pollForm.options.length < 6 && <button onClick={() => setPollForm({ ...pollForm, options: [...pollForm.options, ''] })} className="px-2 py-1 rounded bg-slate-700 text-slate-200 text-[10px]">+ Option</button>}
            <input placeholder="City (blank=global)" value={pollForm.city} onChange={(e) => setPollForm({ ...pollForm, city: e.target.value })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px] flex-1" />
            <input type="number" placeholder="Hours" value={pollForm.expires_hours} onChange={(e) => setPollForm({ ...pollForm, expires_hours: parseInt(e.target.value) || 72 })} className="w-20 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          </div>
          <button onClick={createPoll} data-testid="poll-create" className="w-full px-3 py-1.5 rounded bg-fuchsia-500 text-white text-xs font-black">🗳️ Launch Poll</button>
        </div>
        <div className="max-h-52 overflow-y-auto space-y-1">
          {polls.map((p) => {
            const total = Object.values(p.results || {}).reduce((s, v) => s + v, 0);
            return (
              <div key={p.id} className="rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`poll-row-${p.id}`}>
                <div className="flex items-center gap-2 mb-1"><span className="font-black flex-1 truncate">{p.question}</span><span className="text-[10px] opacity-70">{total} votes · {p.city || 'GLOBAL'}</span><button onClick={() => delPoll(p)} className="text-red-300 text-xs">✕</button></div>
                <div className="text-[10px] opacity-70">{p.options.map((o, i) => `${o} (${p.results?.[i] || 0})`).join(' · ')}</div>
              </div>
            );
          })}
          {polls.length === 0 && <div className="text-xs opacity-60 text-center py-4">No polls yet</div>}
        </div>
      </div>

      {/* Content Expiry Master Control */}
      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">⏱️ Content Expiry Control ({contentAll.length} recent approved)</div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {contentAll.map((c) => {
            const exp = c.display_expiry ? new Date(c.display_expiry) : null;
            const remaining = exp ? Math.max(0, Math.round((exp - new Date()) / (1000 * 60 * 60 * 24))) : '—';
            return (
              <div key={c.id} className="rounded bg-slate-800/60 p-1.5 flex items-center gap-2 text-[11px]" data-testid={`expiry-row-${c.id}`}>
                <div className="flex-1 min-w-0"><div className="font-black truncate flex items-center gap-1">{c.is_pinned && <span className="text-red-300 animate-pulse">📌</span>}{c.title}</div><div className="opacity-70 text-[10px]">{c.city} · {c.category} · {c.content_type}</div></div>
                <span className={`text-[10px] font-mono ${remaining === 0 ? 'text-red-300' : remaining <= 2 ? 'text-amber-300' : 'text-emerald-300'}`}>{remaining}d</span>
                <button onClick={() => togglePin(c)} data-testid={`pin-btn-${c.id}`} className={`text-[10px] px-2 py-0.5 rounded font-black ${c.is_pinned ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-200'}`}>{c.is_pinned ? '📌 Unpin' : '📌 Pin'}</button>
                <button onClick={() => adjustExpiry(c, false)} data-testid={`expiry-btn-${c.id}`} className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/25 text-cyan-200 font-black">± Days</button>
              </div>
            );
          })}
          {contentAll.length === 0 && <div className="text-xs opacity-60 text-center py-4">No approved content</div>}
        </div>
      </div>

      {/* Reporters KYC */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🪪 Reporters — KYC Queue</div>
          <select value={kycFilter} onChange={(e) => setKycFilter(e.target.value)} className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700">
            {['PENDING', 'APPROVED', 'REJECTED', 'NOT_SUBMITTED', ''].map((k) => <option key={k} value={k}>{k || 'ALL'}</option>)}
          </select>
        </div>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {reporters.map((r) => (
            <div key={r.user_id} className="rounded-lg border border-slate-700 bg-slate-800/50 p-2" data-testid={`asn-rep-${r.user_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                {r.photo_url && <img src={r.photo_url} alt="" className="w-10 h-10 rounded object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black">{r.full_name || '—'} <span className="opacity-70">@{r.username}</span></div>
                  <div className="text-[10px] opacity-70">{r.mobile || '—'} · {r.city || '—'} · Sub: {r.subscription_status} · Directs: {/* placeholder */}</div>
                  {r.kyc_doc_url && <a href={r.kyc_doc_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-300 underline">View KYC doc</a>}
                </div>
                <span className={`text-[9px] px-2 py-0.5 rounded font-black ${kycColor[r.kyc_status] || kycColor.NOT_SUBMITTED}`}>{r.kyc_status}</span>
                {r.kyc_status === 'PENDING' && (
                  <>
                    <button onClick={() => kycAction(r, 'approve')} data-testid={`asn-kyc-approve-${r.user_id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/25 text-emerald-300 font-black">✓ Approve</button>
                    <button onClick={() => kycAction(r, 'reject', window.prompt('Reject reason?') || 'Insufficient docs')} data-testid={`asn-kyc-reject-${r.user_id}`} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300 font-black">✕ Reject</button>
                  </>
                )}
                {!r.banned && r.kyc_status !== 'PENDING' && <button onClick={() => banReporter(r)} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300">Ban</button>}
              </div>
            </div>
          ))}
          {reporters.length === 0 && <div className="text-xs opacity-60 text-center py-4">No reporters</div>}
        </div>
      </div>

      {/* Content Moderation */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">📝 Pending Content ({content.length})</div>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {content.map((c) => (
            <div key={c.id} className="rounded-lg border border-slate-700 bg-slate-800/50 p-2">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                <div className="text-xs font-black flex-1 min-w-0 truncate">{c.title}</div>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-black">{c.content_type}</span>
              </div>
              <div className="text-[10px] opacity-70">{c.city} · {c.category} · @{c.username} · {new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              {c.body && <div className="text-[10px] opacity-80 mt-1 line-clamp-2">{c.body}</div>}
              <div className="flex gap-1 mt-2">
                <button onClick={() => moderateContent(c, 'approve')} data-testid={`asn-mod-approve-${c.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/25 text-emerald-300 font-black">✓ Approve</button>
                <button onClick={() => moderateContent(c, 'reject')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300 font-black">✕ Reject</button>
                <button onClick={() => moderateContent(c, 'delete')} className="text-[10px] px-2 py-0.5 rounded bg-slate-600 text-slate-300 font-black">🗑 Delete</button>
              </div>
            </div>
          ))}
          {content.length === 0 && <div className="text-xs opacity-60 text-center py-4">No pending content</div>}
        </div>
      </div>

      {/* Ads Moderation */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2">📢 Pending Ads ({ads.length})</div>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {ads.map((a) => (
            <div key={a.id} className="rounded-lg border border-slate-700 bg-slate-800/50 p-2 flex items-center gap-2">
              <img src={a.banner_url} alt="" className="w-20 h-12 object-cover rounded" onError={(e) => e.currentTarget.style.display = 'none'} />
              <div className="flex-1 min-w-0 text-[11px]">
                <div className="font-black">{a.target_city} · {a.slot} · {a.days}d · {a.price_at} AT</div>
                <div className="opacity-70 text-[10px]">By @{a.advertiser_username} {a.referred_by_reporter && <>· Ref: {a.referred_by_reporter}</>}</div>
              </div>
              <button onClick={() => moderateAd(a, 'approve')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/25 text-emerald-300 font-black">✓</button>
              <button onClick={() => moderateAd(a, 'reject')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300 font-black">✕</button>
            </div>
          ))}
          {ads.length === 0 && <div className="text-xs opacity-60 text-center py-4">No pending ads</div>}
        </div>
      </div>

      {/* Breaking News Ticker */}
      <div className="rounded-2xl border border-red-400/30 bg-red-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2">⚡ Breaking News Ticker</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
          <input placeholder="Ticker text" value={tickerForm.text} onChange={(e) => setTickerForm({ ...tickerForm, text: e.target.value })} data-testid="tick-text" className="sm:col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="City (blank = global)" value={tickerForm.city} onChange={(e) => setTickerForm({ ...tickerForm, city: e.target.value })} data-testid="tick-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="number" placeholder="Hours" value={tickerForm.expires_hours} onChange={(e) => setTickerForm({ ...tickerForm, expires_hours: parseInt(e.target.value) || 24 })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <button onClick={pushTicker} data-testid="tick-push" className="w-full sm:w-auto px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-black">🚀 Push Live Ticker</button>
        <div className="mt-3 space-y-1">
          {tickers.map((t) => (
            <div key={t.id} className="flex items-center gap-2 rounded bg-slate-800/60 p-1.5 text-[11px]">
              <span className="text-[9px] px-1 rounded bg-red-500 text-white font-black">LIVE</span>
              <span className="flex-1 truncate">{t.text}</span>
              <span className="text-[9px] opacity-70">{t.city || 'GLOBAL'} · till {new Date(t.expires_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              <button onClick={() => delTicker(t)} className="text-red-300 text-[10px]">✕</button>
            </div>
          ))}
        </div>
      </div>

      {/* Direct Publish */}
      <div className="rounded-2xl border border-blue-400/30 bg-blue-500/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-blue-300 font-black mb-2">✍️ Admin Direct Publish (Bypass moderation)</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select value={directForm.content_type} onChange={(e) => setDirectForm({ ...directForm, content_type: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="POST">Post</option><option value="PACK">Pack</option><option value="VIDEO">Video</option>
          </select>
          <input placeholder="City" value={directForm.city} onChange={(e) => setDirectForm({ ...directForm, city: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <input placeholder="Title" value={directForm.title} onChange={(e) => setDirectForm({ ...directForm, title: e.target.value })} className="w-full mb-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <textarea placeholder="Body" value={directForm.body} onChange={(e) => setDirectForm({ ...directForm, body: e.target.value })} rows={3} className="w-full mb-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs resize-none" />
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input placeholder="Image URL" value={directForm.media_url} onChange={(e) => setDirectForm({ ...directForm, media_url: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="YouTube URL" value={directForm.youtube_url} onChange={(e) => setDirectForm({ ...directForm, youtube_url: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <button onClick={directPublish} className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-black">🚀 Direct Publish</button>
      </div>
    </div>
  );
}


// ===== Health Admin Panel =====
function HealthAdminPanel() {
  const [users, setUsers] = useState([]);
  const [days, setDays] = useState(30);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/health/users', { params: { limit: 200 } });
      setUsers(r.data?.users || []);
    } catch { toast.error('Health users load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const activate = async (uid) => {
    if (!window.confirm(`Grant ${days} days FREE Health Tracker access?`)) return;
    try {
      await api.post(`/admin/health/users/${uid}/activate`, { days });
      toast.success('Activated'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="rounded-2xl border-2 border-emerald-400/50 bg-emerald-500/10 p-4 mb-4" data-testid="admin-health-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-emerald-300 font-black">💗 Health Tracker Users ({users.length})</div>
        <div className="flex items-center gap-2">
          <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 30)} className="w-16 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" placeholder="Days" />
          <button onClick={load} className="text-[10px] px-2 py-1 rounded bg-white/10 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto text-xs">
        <table className="w-full">
          <thead className="text-left text-[10px] opacity-70 border-b border-slate-700 sticky top-0 bg-slate-900">
            <tr><th className="py-1">User</th><th>Plan</th><th>Status</th><th>Expiry</th><th className="text-right">Action</th></tr>
          </thead>
          <tbody>
            {users.slice(0, 200).map((u) => {
              const sub = u.subscription || {};
              const active = sub.status === 'active' && sub.expiry_at && new Date(sub.expiry_at) > new Date();
              return (
                <tr key={u.user_id} className="border-b border-slate-800" data-testid={`admin-health-row-${u.user_id}`}>
                  <td className="py-1.5">{u.username || u.user_id?.slice(0, 8)}</td>
                  <td className="text-[10px]">{sub.plan_type || '—'}</td>
                  <td><span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-slate-700 text-slate-400'}`}>{active ? 'active' : sub.status || 'inactive'}</span></td>
                  <td className="text-[10px]">{sub.expiry_at ? new Date(sub.expiry_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</td>
                  <td className="text-right"><button onClick={() => activate(u.user_id)} data-testid={`admin-health-activate-${u.user_id}`} className="px-2 py-0.5 rounded bg-emerald-500 text-black text-[10px] font-black">+{days}d</button></td>
                </tr>
              );
            })}
            {users.length === 0 && <tr><td colSpan={5} className="py-6 text-center opacity-60">No Health Tracker users yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== FieldPro Admin Panel =====
function FieldProAdminPanel() {
  const [users, setUsers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [days, setDays] = useState(30);
  const load = useCallback(async () => {
    try {
      const [u, l] = await Promise.all([
        api.get('/admin/fieldpro/users', { params: { limit: 200 } }),
        api.get('/admin/fieldpro/cascade-ledger', { params: { limit: 100 } }),
      ]);
      setUsers(u.data?.users || []);
      setLedger(l.data?.ledger || []);
    } catch { toast.error('FieldPro data load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const activate = async (uid) => {
    if (!window.confirm(`Grant ${days} days FREE FieldPro access?`)) return;
    try { await api.post(`/admin/fieldpro/users/${uid}/activate`, { days }); toast.success('Activated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const suspend = async (uid) => {
    if (!window.confirm('Suspend this FieldPro user?')) return;
    try { await api.post(`/admin/fieldpro/users/${uid}/suspend`); toast.success('Suspended'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="rounded-2xl border-2 border-violet-400/50 bg-violet-500/10 p-4 mb-4" data-testid="admin-fieldpro-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-violet-300 font-black">🧳 FieldPro Users ({users.length})</div>
        <div className="flex items-center gap-2">
          <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 30)} className="w-16 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" placeholder="Days" />
          <button onClick={load} className="text-[10px] px-2 py-1 rounded bg-white/10 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto text-xs mb-3">
        <table className="w-full">
          <thead className="text-left text-[10px] opacity-70 border-b border-slate-700 sticky top-0 bg-slate-900">
            <tr><th className="py-1">User</th><th>Agent</th><th>Status</th><th>Expiry</th><th className="text-right">Action</th></tr>
          </thead>
          <tbody>
            {users.slice(0, 200).map((u) => {
              const sub = u.subscription || {};
              const active = sub.status === 'active' && sub.expiry_at && new Date(sub.expiry_at) > new Date();
              return (
                <tr key={u.user_id} className="border-b border-slate-800" data-testid={`admin-fp-row-${u.user_id}`}>
                  <td className="py-1.5">{u.username || u.user_id?.slice(0, 8)}</td>
                  <td className="text-[10px]">{u.agent_type || '—'}</td>
                  <td><span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-slate-700 text-slate-400'}`}>{active ? 'active' : sub.status || 'expired'}</span></td>
                  <td className="text-[10px]">{sub.expiry_at ? new Date(sub.expiry_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => activate(u.user_id)} data-testid={`admin-fp-activate-${u.user_id}`} className="px-2 py-0.5 rounded bg-violet-500 text-white text-[10px] font-black mr-1">+{days}d</button>
                    {active && <button onClick={() => suspend(u.user_id)} className="px-2 py-0.5 rounded bg-rose-500/60 text-white text-[10px] font-black">✕</button>}
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && <tr><td colSpan={5} className="py-6 text-center opacity-60">No FieldPro users yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-1">🎯 Cascade Ledger (last {ledger.length})</div>
      <div className="max-h-52 overflow-y-auto text-[11px] space-y-1">
        {ledger.slice(0, 50).map((r, i) => (
          <div key={i} className="p-1.5 rounded bg-slate-800/60 flex items-center justify-between">
            <span className="truncate flex-1"><b>{r.type}</b> · {r.event || '—'} · uid:{(r.user_id || '').slice(0, 8)}…</span>
            <span className="text-fuchsia-300 font-black">{r.amount?.toLocaleString()} AT</span>
          </div>
        ))}
        {ledger.length === 0 && <div className="text-center opacity-60 py-3">No cascade events yet.</div>}
      </div>
    </div>
  );
}

// ===== CityPro Admin Panel =====
function CityProAdminPanel() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [cats, setCats] = useState({ defaults: [], custom: [] });
  const [newCat, setNewCat] = useState({ id: '', name: '', icon: '', sub: '' });
  const [freezeCity, setFreezeCity] = useState('');
  const [frozen, setFrozen] = useState([]);
  const [zones, setZones] = useState([]);
  const [newZone, setNewZone] = useState({ city: '', name: '', center_lat: '', center_lng: '', radius_km: 5, active: true });
  // NEW: KYC, Rates, Banners
  const [kycRows, setKycRows] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateEdit, setRateEdit] = useState({});   // { [service_id]: { base_at, per_km } }
  const [banners, setBanners] = useState([]);
  const [newBanner, setNewBanner] = useState({ title: '', image_url: '', link_url: '', priority: 50, active: true });
  // v3: WA list, WA group, plan limits, audit
  const [waList, setWaList] = useState([]);
  const [newWaNum, setNewWaNum] = useState('');
  const [waGroup, setWaGroup] = useState('');
  const [planLimits, setPlanLimits] = useState({});   // { basic: {product_limit, at_month}, ... }
  const [audit, setAudit] = useState({ tasks: [], counts: {} });

  const load = useCallback(async () => {
    try {
      const [s, o, m, c, fz, z, k, r, b, w, wg, pl, au, cfg] = await Promise.all([
        api.get('/admin/citypro/stats'),
        api.get('/admin/citypro/orders', { params: { limit: 60 } }),
        api.get('/admin/citypro/merchants', { params: { limit: 60 } }),
        api.get('/admin/citypro/categories'),
        api.get('/admin/citypro/frozen-cities'),
        api.get('/admin/citypro/zones'),
        api.get('/admin/citypro/kyc'),
        api.get('/admin/citypro/rates'),
        api.get('/admin/citypro/banners'),
        api.get('/admin/citypro/whatsapp-list'),
        api.get('/citypro/config').catch(() => ({ data: {} })),
        api.get('/citypro/config').catch(() => ({ data: {} })),
        api.get('/admin/citypro/audit'),
        api.get('/citypro/config'),
      ]);
      setStats(s.data?.stats || s.data);
      setOrders(o.data?.orders || []);
      setMerchants(m.data?.merchants || []);
      setCats({ defaults: c.data?.defaults || [], custom: c.data?.custom || [] });
      setFrozen(fz.data?.frozen || []);
      setZones(z.data?.zones || []);
      setKycRows(k.data?.kyc || []);
      setRates(r.data?.services || []);
      setBanners(b.data?.banners || []);
      setWaList(w.data?.numbers || []);
      setWaGroup(wg.data?.wa_group_link || '');
      const pl0 = {};
      (pl.data?.shop_plans || cfg.data?.shop_plans || []).forEach((p) => { pl0[p.id] = { product_limit: p.product_limit, at_month: p.at_month }; });
      setPlanLimits(pl0);
      setAudit(au.data || { tasks: [], counts: {} });
    } catch { toast.error('CityPro admin load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // NEW handlers
  const addWa = async () => {
    const v = newWaNum.replace(/[^\d+]/g, '');
    if (!v) return;
    if (waList.length >= 10) { toast.error('Max 10 numbers'); return; }
    const next = [...waList, v];
    try { await api.post('/admin/citypro/whatsapp-list', { numbers: next }); toast.success('✓ Added'); setNewWaNum(''); load(); }
    catch { toast.error('Failed'); }
  };
  const delWa = async (n) => {
    const next = waList.filter((x) => x !== n);
    try { await api.post('/admin/citypro/whatsapp-list', { numbers: next }); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };
  const saveWaGroup = async () => {
    try { await api.post('/admin/citypro/wa-group', { url: waGroup }); toast.success('✓ Group link saved'); load(); }
    catch { toast.error('Failed'); }
  };
  const savePlanLimit = async (planId) => {
    const p = planLimits[planId] || {};
    try {
      await api.post('/admin/citypro/plans/limits', { plan_id: planId, product_limit: parseInt(p.product_limit || 20), at_month: parseFloat(p.at_month || 0) });
      toast.success(`✓ ${planId} plan saved`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const auditAction = async (t, action) => {
    if (!window.confirm(`${action.toUpperCase()} · ${t.title}?`)) return;
    try { await api.post('/admin/citypro/audit/action', { kind: t.kind, id: t.id, action }); toast.success(`✓ ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const readAudit = () => {
    if (!('speechSynthesis' in window)) { toast.error('Voice not supported'); return; }
    const text = audit.tasks.length === 0 ? 'No pending tasks. All clear.' :
      `${audit.tasks.length} pending tasks. ${audit.tasks.slice(0, 5).map((t, i) => `Task ${i+1}: ${t.title}`).join('. ')}`;
    const u = new SpeechSynthesisUtterance(text); u.lang = 'en-IN'; u.rate = 1;
    window.speechSynthesis.cancel(); window.speechSynthesis.speak(u);
  };
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceHeard, setVoiceHeard] = useState('');
  const voiceRecRef = useRef(null);
  const parseVoiceCmd = (text) => {
    const t = (text || '').toLowerCase().trim();
    // Detect action (English + Hinglish + Hindi transliteration)
    let action = null;
    if (/\b(approve|approv|accept|pass|manzoor|swikaar|स्वीकार|मंजूर|पास)\b/i.test(t)) action = 'approve';
    else if (/\b(reject|rejected|cancel|khaarij|khariz|na manzoor|खारिज|अस्वीकार)\b/i.test(t)) action = 'reject';
    else if (/\b(hold|rok|wait|pause|थाम|रोक|होल्ड)\b/i.test(t)) action = 'hold';
    if (!action) return null;
    // Detect number (digits, ordinal English, Hindi words)
    const map = { one:1, first:1, ek:1, pehla:1, pahla:1, एक:1, पहला:1,
                  two:2, second:2, do:2, dusra:2, doosra:2, दो:2, दूसरा:2,
                  three:3, third:3, teen:3, tisra:3, teesra:3, तीन:3, तीसरा:3,
                  four:4, fourth:4, char:4, chautha:4, चार:4, चौथा:4,
                  five:5, fifth:5, panch:5, paanch:5, paanchwa:5, पांच:5, पाँच:5, पांचवा:5,
                  six:6, chhah:6, chhe:6, छह:6, छठा:6,
                  seven:7, saat:7, सात:7, satwa:7,
                  eight:8, aath:8, आठ:8, आठवा:8,
                  nine:9, nau:9, नौ:9,
                  ten:10, das:10, दस:10 };
    const digitMatch = t.match(/\btask\s*(\d+)|\b(\d+)\s*(?:number|no|task)?\b|\b(\d+)\b/);
    let num = null;
    if (digitMatch) { num = parseInt(digitMatch[1] || digitMatch[2] || digitMatch[3]); }
    if (!num) {
      for (const [k, v] of Object.entries(map)) {
        if (new RegExp(`\\b${k}\\b`, 'i').test(t)) { num = v; break; }
      }
    }
    if (!num || num < 1 || num > audit.tasks.length) return { action, num: null, error: 'no valid task number' };
    return { action, num };
  };
  const startVoiceCmd = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice not supported (Chrome / Edge / Safari)'); return; }
    if (voiceListening) { try { voiceRecRef.current?.stop(); } catch { /* noop */ } return; }
    const rec = new SR();
    rec.lang = 'hi-IN';   // Google STT supports Hinglish under hi-IN
    rec.interimResults = false;
    rec.continuous = true;
    rec.onstart = () => { setVoiceListening(true); toast.success('🎤 Sun raha hoon · say "approve task 1", "reject 2", "hold 3"…'); };
    rec.onresult = async (e) => {
      const last = e.results[e.results.length - 1];
      if (!last?.isFinal) return;
      const heard = last[0].transcript;
      setVoiceHeard(heard);
      const cmd = parseVoiceCmd(heard);
      if (!cmd) { toast.info(`🎤 "${heard}" — command samajh nahi aaya`); return; }
      if (!cmd.num) { toast.error(`🎤 "${heard}" — task number nahi mila`); return; }
      const t = audit.tasks[cmd.num - 1];
      if (!t) { toast.error(`Task ${cmd.num} not found`); return; }
      const u = new SpeechSynthesisUtterance(`Task ${cmd.num} ${cmd.action}ing`); u.lang = 'en-IN';
      window.speechSynthesis.speak(u);
      try {
        await api.post('/admin/citypro/audit/action', { kind: t.kind, id: t.id, action: cmd.action });
        toast.success(`✓ Task ${cmd.num} · ${cmd.action}d`);
        load();
      } catch (er) { toast.error(er?.response?.data?.detail || 'Failed'); }
    };
    rec.onerror = (er) => { setVoiceListening(false); toast.error(`🎤 ${er.error || 'error'}`); };
    rec.onend = () => setVoiceListening(false);
    voiceRecRef.current = rec;
    rec.start();
  };

  const addCat = async () => {
    if (!newCat.id || !newCat.name) { toast.error('id + name required'); return; }
    try {
      await api.post('/admin/citypro/categories', { ...newCat, sub: newCat.sub.split(',').map((s) => s.trim()).filter(Boolean) });
      toast.success('Category saved'); setNewCat({ id: '', name: '', icon: '', sub: '' }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delCat = async (id) => {
    if (!window.confirm(`Delete category ${id}?`)) return;
    try { await api.delete(`/admin/citypro/categories/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };
  const forceCancel = async (id) => {
    if (!window.confirm('Force cancel this order? (auto refund)')) return;
    try { await api.post(`/admin/citypro/orders/${id}/force-cancel`); toast.success('Cancelled + refunded'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleFreeze = async (city, currentlyFrozen) => {
    if (!city) return;
    try {
      await api.post('/admin/citypro/city-freeze', { city, frozen: !currentlyFrozen });
      toast.success(currentlyFrozen ? `❄️ Unfrozen ${city}` : `🔒 Frozen ${city}`);
      setFreezeCity(''); load();
    } catch { toast.error('Failed'); }
  };

  const saveZone = async () => {
    if (!newZone.city || !newZone.name || !newZone.center_lat || !newZone.center_lng) { toast.error('City/Name/Lat/Lng required'); return; }
    try {
      await api.post('/admin/citypro/zones', {
        ...newZone,
        center_lat: parseFloat(newZone.center_lat), center_lng: parseFloat(newZone.center_lng),
        radius_km: parseFloat(newZone.radius_km),
      });
      toast.success('✓ Zone saved');
      setNewZone({ city: '', name: '', center_lat: '', center_lng: '', radius_km: 5, active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delZone = async (id) => {
    if (!window.confirm('Delete zone?')) return;
    try { await api.delete(`/admin/citypro/zones/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };
  const useMyGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => setNewZone((z) => ({ ...z, center_lat: p.coords.latitude.toFixed(5), center_lng: p.coords.longitude.toFixed(5) })));
  };

  // KYC actions
  const kycAction = async (id, action) => {
    const reason = action === 'reject' ? window.prompt('Reason for rejection?') || '' : '';
    try { await api.post(`/admin/citypro/kyc/${id}/action`, { action, reason }); toast.success(`✓ KYC ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  // Rate edits
  const saveRate = async (svc) => {
    const patch = rateEdit[svc.id] || {};
    const payload = { service_id: svc.id, base_at: parseFloat(patch.base_at ?? svc.base_at), per_km: parseFloat(patch.per_km ?? svc.per_km ?? 0) };
    try { await api.post('/admin/citypro/rates', payload); toast.success(`✓ ${svc.name} rate saved`); setRateEdit({ ...rateEdit, [svc.id]: undefined }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const resetRate = async (id) => {
    try { await api.delete(`/admin/citypro/rates/${id}`); toast.success('Reset to default'); load(); }
    catch { toast.error('Failed'); }
  };
  // Banner CRUD
  const saveBanner = async () => {
    if (!newBanner.title || !newBanner.image_url) { toast.error('Title + image URL required'); return; }
    try {
      await api.post('/admin/citypro/banners', { ...newBanner, priority: parseInt(newBanner.priority || 50) });
      toast.success('✓ Banner saved'); setNewBanner({ title: '', image_url: '', link_url: '', priority: 50, active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delBanner = async (id) => {
    if (!window.confirm('Delete banner?')) return;
    try { await api.delete(`/admin/citypro/banners/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-citypro-panel">
      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ['👥 Profiles', stats.profiles], ['🏬 Merchants', stats.merchants],
            ['🚕 Drivers', stats.drivers], ['💸 Cash Centers', stats.cash_centers],
            ['📋 Orders', stats.orders_total], ['✅ Completed', stats.orders_completed],
            ['⏳ Pending', stats.orders_pending], ['📅 Today', stats.orders_today],
            ['🛍️ Products', stats.products], ['🏙️ Cities', stats.cities],
            ['📂 Categories', stats.categories], ['💰 Cash Logs', stats.cash_logs],
          ].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-teal-500/10 border border-teal-400/30 p-2 text-center">
              <div className="text-[10px] opacity-80">{l}</div>
              <div className="text-lg font-black text-teal-300">{v ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* City Freeze */}
      <div className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-3" data-testid="admin-cp-freeze">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2">❄️ City Freeze (Emergency Override)</div>
        <div className="flex gap-2 mb-2">
          <input placeholder="City name" value={freezeCity} onChange={(e) => setFreezeCity(e.target.value)} className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={() => toggleFreeze(freezeCity, false)} className="px-3 py-1.5 rounded bg-rose-500 text-white text-xs font-black">🔒 Freeze</button>
        </div>
        <div className="text-[10px] opacity-80">Currently frozen: {frozen.length === 0 ? '— none —' : frozen.map((c) => (
          <button key={c} onClick={() => toggleFreeze(c, true)} className="mr-1 px-1.5 py-0.5 rounded bg-rose-500/25 text-rose-200 font-black">{c} ✕</button>
        ))}</div>
      </div>

      {/* Delivery Zones */}
      <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/10 p-3" data-testid="admin-cp-zones">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">🗺️ Delivery / Service Zones · {zones.length}</div>
        <div className="text-[10px] opacity-80 mb-2">Zone ke andar ke partners hi orders dekhenge · lat/lng se center + radius (km) set karo. GPS button current location le lega.</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mb-2">
          <input placeholder="City" value={newZone.city} onChange={(e) => setNewZone({ ...newZone, city: e.target.value })} data-testid="admin-zone-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Zone name" value={newZone.name} onChange={(e) => setNewZone({ ...newZone, name: e.target.value })} data-testid="admin-zone-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Center Lat" value={newZone.center_lat} onChange={(e) => setNewZone({ ...newZone, center_lat: e.target.value })} data-testid="admin-zone-lat" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Center Lng" value={newZone.center_lng} onChange={(e) => setNewZone({ ...newZone, center_lng: e.target.value })} data-testid="admin-zone-lng" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="number" placeholder="Radius (km)" value={newZone.radius_km} onChange={(e) => setNewZone({ ...newZone, radius_km: e.target.value })} data-testid="admin-zone-radius" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <label className="flex items-center gap-1 text-[11px] px-2"><input type="checkbox" checked={newZone.active} onChange={(e) => setNewZone({ ...newZone, active: e.target.checked })} /> Active</label>
          <button onClick={useMyGps} className="px-2 py-1.5 rounded bg-slate-800 text-cyan-200 text-[10px] font-black">📍 Use my GPS</button>
          <button onClick={saveZone} data-testid="admin-zone-save" className="px-2 py-1.5 rounded bg-cyan-500 text-black text-[10px] font-black">➕ Save Zone</button>
        </div>
        <div className="max-h-40 overflow-y-auto text-xs">
          <table className="w-full">
            <thead className="text-left text-[10px] opacity-70 border-b border-slate-700 sticky top-0 bg-slate-900">
              <tr><th className="py-1">City</th><th>Name</th><th>Center</th><th>Radius</th><th>Active</th><th></th></tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id} className="border-b border-slate-800" data-testid={`admin-zone-${z.id}`}>
                  <td className="py-1.5 text-[10px]">{z.city}</td>
                  <td className="text-[10px]">{z.name}</td>
                  <td className="text-[10px] font-mono">{z.center_lat?.toFixed(3)}, {z.center_lng?.toFixed(3)}</td>
                  <td className="text-[10px]">{z.radius_km}km</td>
                  <td><span className={`text-[10px] px-1.5 rounded ${z.active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-slate-700'}`}>{z.active ? '🟢' : '⚪'}</span></td>
                  <td className="text-right"><button onClick={() => delZone(z.id)} className="text-rose-300 text-[10px]">✕</button></td>
                </tr>
              ))}
              {zones.length === 0 && <tr><td colSpan={6} className="text-center opacity-60 py-3">No zones yet — pehla zone add karo!</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Categories manager */}
      <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3" data-testid="admin-cp-cats">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">📂 Merchant Categories ({cats.defaults.length} default + {cats.custom.length} custom)</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mb-2 max-h-40 overflow-y-auto">
          {cats.defaults.map((c) => (
            <div key={c.id} className="rounded bg-slate-800/60 border border-slate-700 p-1.5 text-[10px]">
              <b>{c.icon} {c.name}</b>
              <div className="opacity-60 text-[9px]">{(c.sub || []).length} sub · locked</div>
            </div>
          ))}
          {cats.custom.map((c) => (
            <div key={c.id} className="rounded bg-emerald-500/10 border border-emerald-400/30 p-1.5 text-[10px]">
              <div className="flex items-center justify-between gap-1">
                <b className="truncate">{c.icon} {c.name}</b>
                <button onClick={() => delCat(c.id)} className="text-rose-300">✕</button>
              </div>
              <div className="opacity-60 text-[9px]">{(c.sub || []).length} sub · custom</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1 mb-2">
          <input placeholder="id (e.g. jewellery)" value={newCat.id} onChange={(e) => setNewCat({ ...newCat, id: e.target.value.toLowerCase().replace(/\s/g, '_') })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Icon emoji" value={newCat.icon} onChange={(e) => setNewCat({ ...newCat, icon: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Sub-cats (comma-separated)" value={newCat.sub} onChange={(e) => setNewCat({ ...newCat, sub: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <button onClick={addCat} data-testid="admin-cp-addcat" className="w-full px-3 py-1.5 rounded bg-amber-500 text-black text-xs font-black">➕ Add / Update Custom Category</button>
      </div>

      {/* Merchants */}
      <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-3" data-testid="admin-cp-merchants">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">🏬 Merchants ({merchants.length})</div>
        <div className="max-h-64 overflow-y-auto text-xs">
          <table className="w-full">
            <thead className="text-left text-[10px] opacity-70 border-b border-slate-700 sticky top-0 bg-slate-900">
              <tr><th className="py-1">Shop</th><th>Category</th><th>City</th><th>Products</th><th>Active</th></tr>
            </thead>
            <tbody>
              {merchants.map((m) => (
                <tr key={m.user_id} className="border-b border-slate-800">
                  <td className="py-1.5 truncate">{m.shop_name || `@${m.username}`}</td>
                  <td className="text-[10px]">{m.merchant_category || '—'}</td>
                  <td className="text-[10px]">{m.city || '—'}</td>
                  <td className="text-[10px] text-emerald-300 font-black">{m.product_count}</td>
                  <td><span className={`text-[10px] px-1.5 rounded ${m.active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-slate-700 text-slate-400'}`}>{m.active ? '🟢' : '⚪'}</span></td>
                </tr>
              ))}
              {merchants.length === 0 && <tr><td colSpan={5} className="text-center opacity-60 py-3">No merchants yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* KYC Requests */}
      <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3" data-testid="admin-cp-kyc">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🆔 KYC Requests ({kycRows.length})</div>
          <button onClick={load} className="text-[10px] px-2 py-1 rounded bg-white/10">↻</button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {kycRows.map((k) => (
            <div key={k.id} className="rounded bg-slate-800/60 p-2 text-xs" data-testid={`admin-cp-kyc-${k.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <b className="text-amber-200">@{k.username}</b>
                <span className="px-1.5 py-0.5 rounded bg-slate-700 text-[9px] font-black">{k.role}</span>
                <span className="text-[10px] opacity-80">{k.full_name} · {k.doc_type} {k.doc_number}</span>
                <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-black ${k.status === 'approved' ? 'bg-emerald-500/30 text-emerald-200' : k.status === 'rejected' ? 'bg-rose-500/30 text-rose-200' : 'bg-amber-500/30 text-amber-100'}`}>{k.status}</span>
              </div>
              <div className="text-[10px] opacity-70 mt-0.5">📍 {k.address || '—'} · {new Date(k.submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              {(k.doc_image_url || k.selfie_url) && (
                <div className="flex gap-1 mt-1">
                  {k.doc_image_url && <a href={k.doc_image_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/25 text-cyan-200">📄 Doc</a>}
                  {k.selfie_url && <a href={k.selfie_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/25 text-cyan-200">🤳 Selfie</a>}
                </div>
              )}
              {k.status === 'pending' && (
                <div className="flex gap-1 mt-1">
                  <button onClick={() => kycAction(k.id, 'approve')} data-testid={`admin-kyc-approve-${k.id}`} className="px-2 py-0.5 rounded bg-emerald-500 text-black text-[10px] font-black">✓ Approve</button>
                  <button onClick={() => kycAction(k.id, 'reject')} data-testid={`admin-kyc-reject-${k.id}`} className="px-2 py-0.5 rounded bg-rose-500 text-white text-[10px] font-black">✕ Reject</button>
                </div>
              )}
              {k.reason && <div className="text-[10px] text-rose-300 mt-0.5">Reason: {k.reason}</div>}
            </div>
          ))}
          {kycRows.length === 0 && <div className="text-center opacity-60 py-3">No KYC submitted yet</div>}
        </div>
      </div>

      {/* Rate Editor */}
      <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/10 p-3" data-testid="admin-cp-rates">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">⚙️ Service Rate Editor · base_at + per_km</div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] opacity-70"><tr><th className="text-left">Service</th><th>Base AT</th><th>Per Km</th><th></th></tr></thead>
            <tbody>
              {rates.map((svc) => (
                <tr key={svc.id} className="border-t border-white/5" data-testid={`admin-rate-${svc.id}`}>
                  <td className="py-1"><b>{svc.icon} {svc.name}</b><span className="text-[9px] opacity-60 ml-1">{svc.id}</span></td>
                  <td><input type="number" step="0.5" defaultValue={svc.base_at} onChange={(e) => setRateEdit((r) => ({ ...r, [svc.id]: { ...(r[svc.id] || {}), base_at: e.target.value } }))} data-testid={`admin-rate-base-${svc.id}`} className="w-16 px-1 py-0.5 rounded bg-slate-800 border border-slate-700 text-[11px] text-center" /></td>
                  <td><input type="number" step="0.1" defaultValue={svc.per_km || 0} onChange={(e) => setRateEdit((r) => ({ ...r, [svc.id]: { ...(r[svc.id] || {}), per_km: e.target.value } }))} data-testid={`admin-rate-km-${svc.id}`} className="w-16 px-1 py-0.5 rounded bg-slate-800 border border-slate-700 text-[11px] text-center" /></td>
                  <td className="text-right">
                    <button onClick={() => saveRate(svc)} data-testid={`admin-rate-save-${svc.id}`} className="px-2 py-0.5 rounded bg-cyan-500 text-black text-[10px] font-black">Save</button>
                    <button onClick={() => resetRate(svc.id)} className="ml-1 px-1.5 py-0.5 rounded bg-slate-700 text-slate-200 text-[10px]">↺</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Banners CRUD (Classified Sponsored Ads) */}
      <div className="rounded-2xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3" data-testid="admin-cp-banners">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">📣 Showcase Banners CRUD ({banners.length})</div>
        <div className="grid grid-cols-2 gap-1.5 mb-2">
          <input placeholder="Title" value={newBanner.title} onChange={(e) => setNewBanner({ ...newBanner, title: e.target.value })} data-testid="admin-banner-title" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
          <input placeholder="Image URL (3:1 aspect)" value={newBanner.image_url} onChange={(e) => setNewBanner({ ...newBanner, image_url: e.target.value })} data-testid="admin-banner-img" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
          <input placeholder="Link URL (optional)" value={newBanner.link_url} onChange={(e) => setNewBanner({ ...newBanner, link_url: e.target.value })} data-testid="admin-banner-link" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="number" placeholder="Priority (higher = first)" value={newBanner.priority} onChange={(e) => setNewBanner({ ...newBanner, priority: e.target.value })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <button onClick={saveBanner} data-testid="admin-banner-save" className="w-full px-3 py-1.5 rounded bg-fuchsia-500 text-white text-xs font-black mb-2">+ Add Banner</button>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {banners.map((b) => (
            <div key={b.id} className="rounded bg-slate-800/60 p-1.5 flex items-center gap-2" data-testid={`admin-banner-${b.id}`}>
              <div className="w-16 h-8 bg-slate-900 bg-cover bg-center rounded" style={{ backgroundImage: `url(${b.image_url})` }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate">{b.title}</div>
                <div className="text-[9px] opacity-70">priority {b.priority} · {b.active ? '🟢' : '⚪'}</div>
              </div>
              <button onClick={() => delBanner(b.id)} data-testid={`admin-banner-del-${b.id}`} className="text-red-300 text-sm">✕</button>
            </div>
          ))}
          {banners.length === 0 && <div className="text-center opacity-60 py-2 text-[11px]">No banners yet</div>}
        </div>
      </div>

      {/* System Auditor */}
      <div className="rounded-2xl border-2 border-red-400/50 bg-gradient-to-br from-red-950/40 to-slate-900 p-3" data-testid="admin-cp-audit">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-black">🛰️ System Auditor · Pending Tasks ({audit.counts?.total || 0})</div>
          <div className="flex gap-1">
            <button onClick={load} className="text-[10px] px-2 py-1 rounded bg-white/10">↻ Rescan</button>
            <button onClick={readAudit} data-testid="admin-cp-audit-voice" className="text-[10px] px-2 py-1 rounded bg-rose-500 text-white font-black">🔊 Read Aloud</button>
            <button onClick={startVoiceCmd} data-testid="admin-cp-audit-listen"
                    className={`text-[10px] px-2 py-1 rounded font-black ${voiceListening ? 'bg-red-500 text-white animate-pulse' : 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white'}`}>
              {voiceListening ? '⏹ Stop Listening' : '🎤 Voice Command'}
            </button>
            <button onClick={() => window.speechSynthesis?.cancel()} className="text-[10px] px-2 py-1 rounded bg-slate-700 text-slate-200">⏹</button>
          </div>
        </div>
        {voiceListening && (
          <div className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/10 p-2 mb-2 text-[10px]" data-testid="admin-voice-hint">
            🎤 <b>Sun raha hoon</b> · Bol sakte ho: {`"approve task 1"`} · {`"task 2 reject karo"`} · {`"पहला approve"`} · {`"do reject"`} · {`"hold three"`}
            {voiceHeard && <div className="mt-1 text-fuchsia-200">Last heard: <b>{voiceHeard}</b></div>}
          </div>
        )}
        <div className="grid grid-cols-4 gap-1 mb-2">
          {[
            ['🆔 KYC', audit.counts?.kyc_pending],
            ['⏳ Orders', audit.counts?.orders_stuck],
            ['📩 Reqs', audit.counts?.requests_open],
            ['🚀 Boost', audit.counts?.boost_expired],
          ].map(([l, v]) => (
            <div key={l} className="rounded bg-red-500/10 border border-red-400/30 p-1 text-center text-[10px]">
              <div className="opacity-80">{l}</div><div className="text-lg font-black text-red-300">{v ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {(audit.tasks || []).map((t) => (
            <div key={`${t.kind}-${t.id}`} className="rounded bg-slate-800/70 p-2 text-xs flex items-start gap-2" data-testid={`admin-audit-${t.kind}-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-black truncate">{t.title}</div>
                <div className="text-[9px] opacity-60">{t.kind} · {new Date(t.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {t.actions.includes('approve') && <button onClick={() => auditAction(t, 'approve')} data-testid={`audit-approve-${t.id}`} className="px-2 py-0.5 rounded bg-emerald-500 text-black text-[10px] font-black">✓</button>}
                {t.actions.includes('hold') && <button onClick={() => auditAction(t, 'hold')} data-testid={`audit-hold-${t.id}`} className="px-2 py-0.5 rounded bg-amber-500 text-black text-[10px] font-black">⏸</button>}
                {t.actions.includes('reject') && <button onClick={() => auditAction(t, 'reject')} data-testid={`audit-reject-${t.id}`} className="px-2 py-0.5 rounded bg-red-500 text-white text-[10px] font-black">✕</button>}
              </div>
            </div>
          ))}
          {(audit.tasks || []).length === 0 && <div className="text-center opacity-60 py-4">✅ All clear · no pending tasks</div>}
        </div>
      </div>

      {/* Admin WhatsApp List (custom request routing) */}
      <div className="rounded-2xl border border-green-400/40 bg-green-500/10 p-3" data-testid="admin-cp-wa-list">
        <div className="text-[10px] uppercase tracking-widest text-green-300 font-black mb-2">📱 Admin WhatsApp Numbers ({waList.length}/10) · Custom Requests route here</div>
        <div className="flex gap-1 mb-2">
          <input placeholder="e.g. 919157388830" value={newWaNum} onChange={(e) => setNewWaNum(e.target.value)} data-testid="admin-wa-num-input" className="flex-1 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={addWa} data-testid="admin-wa-add" className="px-3 py-1 rounded bg-green-500 text-white text-xs font-black">+ Add</button>
        </div>
        <div className="flex flex-wrap gap-1">
          {waList.map((n) => (
            <span key={n} className="text-[10px] px-2 py-0.5 rounded bg-green-500/25 text-green-100 font-black flex items-center gap-1" data-testid={`admin-wa-num-${n}`}>
              {n}
              <button onClick={() => delWa(n)} className="text-red-300 hover:text-red-500">✕</button>
            </span>
          ))}
          {waList.length === 0 && <span className="text-[10px] opacity-60">No numbers added · falls back to default 919157388830</span>}
        </div>
      </div>

      {/* Delivery Partner WA Group Link */}
      <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-3" data-testid="admin-cp-wa-group">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">👥 Delivery Partner WhatsApp Group Invite Link</div>
        <div className="flex gap-1">
          <input placeholder="https://chat.whatsapp.com/…" value={waGroup} onChange={(e) => setWaGroup(e.target.value)} data-testid="admin-wa-group-input" className="flex-1 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={saveWaGroup} data-testid="admin-wa-group-save" className="px-3 py-1 rounded bg-emerald-500 text-black text-xs font-black">Save</button>
        </div>
      </div>

      {/* Plan Limits Editor */}
      <div className="rounded-2xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3" data-testid="admin-cp-plan-limits">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">💎 Shop Plan Limits · products + price</div>
        <div className="grid grid-cols-1 gap-2">
          {['basic', 'premium', 'enterprise'].map((pid) => (
            <div key={pid} className="flex items-center gap-2 rounded bg-slate-800/60 p-2 text-xs">
              <div className="w-24 font-black uppercase text-fuchsia-200">{pid}</div>
              <label className="flex-1 flex flex-col text-[10px]">
                <span className="opacity-70">Product limit</span>
                <input type="number" value={planLimits[pid]?.product_limit ?? ''} onChange={(e) => setPlanLimits({ ...planLimits, [pid]: { ...(planLimits[pid] || {}), product_limit: e.target.value } })} data-testid={`admin-plan-lim-${pid}`} className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700" />
              </label>
              <label className="flex-1 flex flex-col text-[10px]">
                <span className="opacity-70">AT / month</span>
                <input type="number" step="0.5" value={planLimits[pid]?.at_month ?? ''} onChange={(e) => setPlanLimits({ ...planLimits, [pid]: { ...(planLimits[pid] || {}), at_month: e.target.value } })} data-testid={`admin-plan-at-${pid}`} className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700" />
              </label>
              <button onClick={() => savePlanLimit(pid)} data-testid={`admin-plan-save-${pid}`} className="px-2 py-1 rounded bg-fuchsia-500 text-white text-[10px] font-black">Save</button>
            </div>
          ))}
        </div>
      </div>

      {/* Orders */}
      <div className="rounded-2xl border border-teal-400/40 bg-teal-500/10 p-3" data-testid="admin-cp-orders">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-teal-300 font-black">📋 Recent Orders ({orders.length})</div>
          <button onClick={load} className="text-[10px] px-2 py-1 rounded bg-white/10">↻</button>
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1 text-xs">
          {orders.map((o) => (
            <div key={o.id} className="rounded bg-slate-800/60 p-2" data-testid={`admin-cp-ord-${o.id}`}>
              <div className="flex items-center gap-2">
                <b className="flex-1 truncate">{o.service_name}</b>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${o.status === 'completed' ? 'bg-emerald-500/30 text-emerald-200' : o.status === 'cancelled' ? 'bg-rose-500/30 text-rose-200' : 'bg-amber-500/30 text-amber-200'}`}>{o.status}</span>
                <span className="text-teal-300 font-black">{o.amount_at} AT</span>
              </div>
              <div className="text-[9px] opacity-70">{new Date(o.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · 🛒 @{o.customer_username} → 👤 @{o.provider_username || '—'}</div>
              {!['completed', 'cancelled'].includes(o.status) && (
                <div className="mt-1 flex flex-wrap gap-1">
                  <button onClick={() => forceCancel(o.id)} className="text-[9px] px-2 py-0.5 rounded bg-rose-500/30 text-rose-200 font-black">✕ Force-Cancel + Refund</button>
                  {['pending', 'booked'].includes(o.status) && (
                    <>
                      <button onClick={async () => { try { await api.post(`/admin/citypro/orders/${o.id}/status`, { status: 'approved' }); toast.success('✓ Approved'); load(); } catch { toast.error('Failed'); } }} data-testid={`admin-order-approve-${o.id}`} className="text-[9px] px-2 py-0.5 rounded bg-emerald-500 text-black font-black">✓ Approve</button>
                      <button onClick={async () => { const r = window.prompt('Reason?') || ''; try { await api.post(`/admin/citypro/orders/${o.id}/status`, { status: 'rejected', reason: r }); toast.success('Rejected'); load(); } catch { toast.error('Failed'); } }} data-testid={`admin-order-reject-${o.id}`} className="text-[9px] px-2 py-0.5 rounded bg-red-500 text-white font-black">✕ Reject</button>
                    </>
                  )}
                  {['approved', 'accepted'].includes(o.status) && (
                    <button onClick={async () => { try { await api.post(`/admin/citypro/orders/${o.id}/status`, { status: 'processing' }); toast.success('→ Processing'); load(); } catch { toast.error('Failed'); } }} className="text-[9px] px-2 py-0.5 rounded bg-violet-500 text-white font-black">⚙️ Processing</button>
                  )}
                  {['processing', 'in_progress'].includes(o.status) && (
                    <button onClick={async () => { try { await api.post(`/admin/citypro/orders/${o.id}/status`, { status: 'on_the_way' }); toast.success('→ On the way'); load(); } catch { toast.error('Failed'); } }} className="text-[9px] px-2 py-0.5 rounded bg-blue-500 text-white font-black">🛵 On the Way</button>
                  )}
                  {['on_the_way'].includes(o.status) && (
                    <button onClick={async () => { try { await api.post(`/admin/citypro/orders/${o.id}/status`, { status: 'delivered' }); toast.success('📦 Delivered'); load(); } catch { toast.error('Failed'); } }} className="text-[9px] px-2 py-0.5 rounded bg-emerald-500 text-black font-black">📦 Delivered</button>
                  )}
                  <button onClick={async () => { const pid = window.prompt('Delivery partner user_id?'); if (!pid) return; try { await api.post(`/admin/citypro/orders/${o.id}/assign`, { provider_id: pid }); toast.success('✓ Assigned'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } }} data-testid={`admin-order-assign-${o.id}`} className="text-[9px] px-2 py-0.5 rounded bg-cyan-500 text-black font-black">🎯 Assign Partner</button>
                </div>
              )}
            </div>
          ))}
          {orders.length === 0 && <div className="text-center opacity-60 py-3">No orders yet</div>}
        </div>
      </div>
    </div>
  );
}


// ============ ASM (Anant Smart Marketing) ADMIN PANEL ============
function ASMAdminPanel() {
  const [stats, setStats] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planEdit, setPlanEdit] = useState({});
  const [withdraws, setWithdraws] = useState([]);
  const load = useCallback(async () => {
    try {
      const [s, p, cfg, w] = await Promise.all([
        api.get('/admin/asm/stats'),
        api.get('/admin/asm/profiles'),
        api.get('/asm/config'),
        api.get('/admin/at-wallet/withdraw').catch(() => ({ data: { requests: [] } })),
      ]);
      setStats(s.data); setProfiles(p.data?.profiles || []); setPlans(cfg.data?.plans || []); setWithdraws(w.data?.requests || []);
    } catch { toast.error('Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggleSuspend = async (id, active) => {
    try { await api.post(`/admin/asm/profiles/${id}/suspend?active=${!active}`); toast.success(active ? 'Suspended' : 'Activated'); load(); }
    catch { toast.error('Failed'); }
  };
  const savePlan = async (planId) => {
    const p = planEdit[planId] || {};
    try {
      await api.post('/admin/asm/plans/limits', {
        plan_id: planId,
        catalog_limit: parseInt(p.catalog_limit || 0) || undefined,
        at_month: p.at_month !== undefined ? parseFloat(p.at_month) : undefined,
        at_year: p.at_year !== undefined ? parseFloat(p.at_year) : undefined,
      });
      toast.success(`✓ ${planId} plan saved`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const wAction = async (id, action) => {
    const ref = action === 'approve' ? window.prompt('Payment reference (UTR/UPI)?') || '' : '';
    const reason = action === 'reject' ? window.prompt('Reason?') || '' : '';
    try { await api.post(`/admin/at-wallet/withdraw/${id}/action`, { action, payment_ref: ref, reason }); toast.success(`${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="admin-asm-panel">
      <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-950/40 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">📣 Anant Smart Marketing · Live Stats</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
          {[['Total', stats?.total], ['Active', stats?.active], ['Items', stats?.catalog_items], ['Views', stats?.views], ['Saves', stats?.saves], ['Shares', stats?.shares]].map(([l, v]) => (
            <div key={l} className="rounded bg-slate-800/60 p-2 text-center">
              <div className="text-[9px] opacity-70 uppercase">{l}</div><div className="text-lg font-black text-fuchsia-300">{v ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">💎 Plan Limits Editor</div>
        {plans.map((pl) => (
          <div key={pl.id} className="flex items-center gap-2 rounded bg-slate-800/60 p-2 text-xs mb-1">
            <div className="w-24 font-black uppercase text-fuchsia-200">{pl.id}</div>
            <label className="flex-1 flex flex-col text-[10px]"><span className="opacity-70">Catalog limit</span>
              <input type="number" defaultValue={pl.catalog_limit} onChange={(e) => setPlanEdit({ ...planEdit, [pl.id]: { ...(planEdit[pl.id] || {}), catalog_limit: e.target.value } })} data-testid={`asm-plan-lim-${pl.id}`} className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700" /></label>
            <label className="flex-1 flex flex-col text-[10px]"><span className="opacity-70">AT / month</span>
              <input type="number" step="0.5" defaultValue={pl.at_month} onChange={(e) => setPlanEdit({ ...planEdit, [pl.id]: { ...(planEdit[pl.id] || {}), at_month: e.target.value } })} className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700" /></label>
            <label className="flex-1 flex flex-col text-[10px]"><span className="opacity-70">AT / year</span>
              <input type="number" step="0.5" defaultValue={pl.at_year} onChange={(e) => setPlanEdit({ ...planEdit, [pl.id]: { ...(planEdit[pl.id] || {}), at_year: e.target.value } })} className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700" /></label>
            <button onClick={() => savePlan(pl.id)} data-testid={`asm-plan-save-${pl.id}`} className="px-2 py-1 rounded bg-fuchsia-500 text-white text-[10px] font-black">Save</button>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">🏢 All Business Cards ({profiles.length})</div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {profiles.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded bg-slate-800/60 p-2 text-xs" data-testid={`asm-prof-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-black truncate">{p.business_name} <span className="text-[9px] opacity-70 ml-1">@{p.username}</span></div>
                <div className="text-[10px] opacity-70">{p.city || '—'} · plan <b className={p.plan_tier === 'pro' ? 'text-emerald-300' : 'text-amber-300'}>{p.plan_tier || 'not subscribed'}</b> · 👀 {p.views || 0} · 💾 {p.saves || 0}</div>
              </div>
              <a href={`/asm/${p.slug}`} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/25 text-cyan-200">🔗 View</a>
              <button onClick={() => toggleSuspend(p.id, p.active)} data-testid={`asm-toggle-${p.id}`} className={`text-[10px] px-2 py-0.5 rounded font-black ${p.active ? 'bg-red-500 text-white' : 'bg-emerald-500 text-black'}`}>{p.active ? 'Suspend' : 'Activate'}</button>
            </div>
          ))}
          {profiles.length === 0 && <div className="text-center opacity-60 py-4">No profiles yet</div>}
        </div>
      </div>

      <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">💸 AT Withdraw Requests ({withdraws.length})</div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {withdraws.map((w) => (
            <div key={w.id} className="rounded bg-slate-800/60 p-2 text-xs" data-testid={`asm-withdraw-${w.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <b className="text-amber-200">@{w.username}</b>
                <span className="text-lg font-black text-amber-100">{w.amount} AT</span>
                <span className="text-[10px] opacity-80">{w.method === 'upi' ? `UPI: ${w.upi_id}` : w.method === 'bank' ? `${w.bank_account} / ${w.bank_ifsc}` : 'Cash'}</span>
                <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-black ${w.status === 'paid' ? 'bg-emerald-500/30 text-emerald-200' : w.status === 'rejected' ? 'bg-rose-500/30 text-rose-200' : 'bg-amber-500/30 text-amber-100'}`}>{w.status}</span>
              </div>
              {w.status === 'pending' && (
                <div className="flex gap-1 mt-1">
                  <button onClick={() => wAction(w.id, 'approve')} data-testid={`asm-w-approve-${w.id}`} className="px-2 py-0.5 rounded bg-emerald-500 text-black text-[10px] font-black">✓ Approve + Pay</button>
                  <button onClick={() => wAction(w.id, 'reject')} data-testid={`asm-w-reject-${w.id}`} className="px-2 py-0.5 rounded bg-red-500 text-white text-[10px] font-black">✕ Reject + Refund</button>
                </div>
              )}
            </div>
          ))}
          {withdraws.length === 0 && <div className="text-center opacity-60 py-3">No withdraw requests</div>}
        </div>
      </div>
    </div>
  );
}



// ============ SOCIAL SHARE ADMIN PANEL ============
function SocialShareAdminPanel() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [board, setBoard] = useState(null);
  const [boardDays, setBoardDays] = useState(30);
  const [tform, setTform] = useState({ label: '', category: 'festival', image_url: '', gradient: 'linear-gradient(135deg,#f59e0b,#dc2626)', sample_caption: 'Shubh Din!', languages: 'hi,en', order: 100 });
  const [userForm, setUserForm] = useState({ user_id: '', action: 'extend_days', days: 30 });
  const load = useCallback(async () => {
    try {
      const [s, cfg, t, u, b] = await Promise.all([
        api.get('/admin/social-share/stats').catch(() => ({ data: null })),
        api.get('/admin/social-share/settings').catch(() => ({ data: null })),
        api.get('/admin/social-share/templates').catch(() => ({ data: { templates: [] } })),
        api.get('/admin/social-share/users').catch(() => ({ data: { users: [] } })),
        api.get(`/admin/avm/at-charge/leaderboard`, { params: { days: boardDays } }).catch(() => ({ data: null })),
      ]);
      setStats(s.data); setSettings(cfg.data); setTemplates(t.data.templates || []); setUsers(u.data.users || []);
      setBoard(b.data);
    } catch { toast.error('Load failed'); }
  }, [boardDays]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try { const r = await api.post('/admin/social-share/settings', settings); setSettings(r.data.settings); toast.success('✅ Settings saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const addTemplate = async () => {
    try {
      const body = { ...tform, languages: tform.languages.split(',').map((x) => x.trim()).filter(Boolean), order: Number(tform.order || 100), active: true };
      await api.post('/admin/social-share/templates', body); toast.success('✅ Template added');
      setTform({ label: '', category: 'festival', image_url: '', gradient: '', sample_caption: '', languages: 'hi,en', order: 100 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delTemplate = async (id) => {
    if (!window.confirm('Delete template?')) return;
    await api.delete(`/admin/social-share/templates/${id}`); load(); toast.success('Deleted');
  };
  const doUserAction = async () => {
    if (!userForm.user_id) { toast.error('user_id required'); return; }
    try {
      const body = { user_id: userForm.user_id, action: userForm.action };
      if (userForm.action === 'extend_days') body.days = Number(userForm.days || 30);
      await api.post('/admin/social-share/users/action', body); toast.success('✅ Done'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!settings || !stats) return <div className="text-center text-fuchsia-300 py-8">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="admin-social-panel">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { k: 'total_users', l: 'Users' }, { k: 'active_subscribers', l: 'Active' },
          { k: 'templates', l: 'Templates' }, { k: 'ai_uses', l: 'AI uses' }, { k: 'saved_posts', l: 'Posts' },
        ].map((m) => (
          <div key={m.k} className="rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 p-3 text-center" data-testid={`admin-social-stat-${m.k}`}>
            <div className="text-2xl font-black text-fuchsia-300">{stats[m.k] || 0}</div>
            <div className="text-[10px] opacity-70 uppercase">{m.l}</div>
          </div>
        ))}
      </div>

      {/* AT-Charge Leaderboard */}
      <div className="rounded-2xl border-2 border-amber-400/40 bg-amber-500/5 p-4 space-y-2" data-testid="admin-at-leaderboard">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black text-amber-300 uppercase">💸 AT-Charge Leaderboard · last {boardDays}d</div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setBoardDays(d)} data-testid={`at-leaderboard-days-${d}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-black ${boardDays === d ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-amber-500/15 border border-amber-400/40 p-2">
            <div className="text-2xl font-black text-amber-200">{board?.total_net_at ?? 0}</div>
            <div className="text-[10px] uppercase opacity-70">Net AT Spent</div>
          </div>
          <div className="rounded-lg bg-emerald-500/15 border border-emerald-400/40 p-2">
            <div className="text-2xl font-black text-emerald-200">{board?.total_calls ?? 0}</div>
            <div className="text-[10px] uppercase opacity-70">AI Calls</div>
          </div>
          <div className="rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/40 p-2">
            <div className="text-2xl font-black text-fuchsia-200">{board?.services ?? 0}</div>
            <div className="text-[10px] uppercase opacity-70">Services</div>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto rounded-lg bg-slate-900/60">
          <table className="w-full text-[10px]">
            <thead className="bg-slate-800/80 sticky top-0">
              <tr className="text-slate-300">
                <th className="text-left px-2 py-1">Service</th>
                <th className="text-right px-2 py-1">Charged</th>
                <th className="text-right px-2 py-1">Refunded</th>
                <th className="text-right px-2 py-1">Net</th>
                <th className="text-right px-2 py-1">Calls</th>
                <th className="text-right px-2 py-1">Success%</th>
              </tr>
            </thead>
            <tbody>
              {(board?.rows || []).map((r) => (
                <tr key={r.service} className="border-t border-slate-800" data-testid={`at-lb-row-${r.service}`}>
                  <td className="px-2 py-1 font-black text-amber-200">{r.service}</td>
                  <td className="px-2 py-1 text-right">{r.charged_at}</td>
                  <td className="px-2 py-1 text-right text-rose-300">{r.refunded_at}</td>
                  <td className="px-2 py-1 text-right font-black text-emerald-300">{r.net_spend_at}</td>
                  <td className="px-2 py-1 text-right">{r.calls}</td>
                  <td className="px-2 py-1 text-right">{r.success_rate}%</td>
                </tr>
              ))}
              {(!board?.rows || board.rows.length === 0) && (
                <tr><td colSpan={6} className="text-center py-3 opacity-60">No AI charges yet in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-fuchsia-500/5 p-4 space-y-2">
        <div className="text-xs font-black text-fuchsia-300 uppercase">⚙️ Settings (Full Control)</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} data-testid="admin-social-enabled" /> Enabled</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!settings.ai_enabled} onChange={(e) => setSettings({ ...settings, ai_enabled: e.target.checked })} data-testid="admin-social-ai-enabled" /> AI Enabled</label>
          <label className="text-xs flex flex-col">Monthly Price (AT)
            <input type="number" value={settings.monthly_price_at} onChange={(e) => setSettings({ ...settings, monthly_price_at: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-social-monthly-price" /></label>
          <label className="text-xs flex flex-col">Monthly Days
            <input type="number" value={settings.monthly_days} onChange={(e) => setSettings({ ...settings, monthly_days: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" /></label>
          <label className="text-xs flex flex-col">Yearly Price (AT)
            <input type="number" value={settings.yearly_price_at} onChange={(e) => setSettings({ ...settings, yearly_price_at: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-social-yearly-price" /></label>
          <label className="text-xs flex flex-col">Yearly Days
            <input type="number" value={settings.yearly_days} onChange={(e) => setSettings({ ...settings, yearly_days: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" /></label>
          <label className="text-xs flex flex-col">AI Charge (AT/call)
            <input type="number" value={settings.ai_charge_at} onChange={(e) => setSettings({ ...settings, ai_charge_at: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-social-ai-charge" /></label>
        </div>
        <button onClick={saveSettings} data-testid="admin-social-save-settings" className="px-4 py-2 rounded-lg bg-fuchsia-500 text-white text-xs font-black">💾 Save Settings</button>
      </div>

      <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-fuchsia-500/5 p-4 space-y-2">
        <div className="text-xs font-black text-fuchsia-300 uppercase">🖼️ Templates ({templates.length}) · Live 1080×1080 Preview</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="grid grid-cols-1 gap-2">
            <input value={tform.label} onChange={(e) => setTform({ ...tform, label: e.target.value })} placeholder="Label" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="admin-social-tpl-label" />
            <input value={tform.category} onChange={(e) => setTform({ ...tform, category: e.target.value })} placeholder="Category" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={tform.languages} onChange={(e) => setTform({ ...tform, languages: e.target.value })} placeholder="Languages (hi,en,mr)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={tform.image_url} onChange={(e) => setTform({ ...tform, image_url: e.target.value })} placeholder="Image URL (optional)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={tform.gradient} onChange={(e) => setTform({ ...tform, gradient: e.target.value })} placeholder="Gradient CSS" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <textarea rows={3} value={tform.sample_caption} onChange={(e) => setTform({ ...tform, sample_caption: e.target.value })} placeholder="Sample caption (Hinglish OK)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs resize-y" />
            <input type="number" value={tform.order} onChange={(e) => setTform({ ...tform, order: e.target.value })} placeholder="Order" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <button onClick={addTemplate} data-testid="admin-social-add-tpl" className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">➕ Add Template</button>
          </div>
          <div className="rounded-xl overflow-hidden border-2 border-fuchsia-400/40 bg-black">
            <SocialTemplateLivePreview form={tform} />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
          {templates.map((t) => (
            <div key={t.id} className="rounded-lg bg-slate-800/60 p-2 text-xs" data-testid={`admin-social-tpl-${t.id}`}>
              {t.image_url && <img src={t.image_url} className="w-full h-24 object-cover rounded mb-1" alt="" />}
              <div className="font-black truncate">{t.label}</div>
              <div className="opacity-70 text-[10px]">{t.category} · {(t.languages || []).join('/')}</div>
              <button onClick={() => delTemplate(t.id)} className="mt-1 text-[10px] text-rose-300">✕ Delete</button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-fuchsia-500/5 p-4 space-y-2">
        <div className="text-xs font-black text-fuchsia-300 uppercase">👤 User Actions ({users.length} users)</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input value={userForm.user_id} onChange={(e) => setUserForm({ ...userForm, user_id: e.target.value })} placeholder="user_id" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="admin-social-user-id" />
          <select value={userForm.action} onChange={(e) => setUserForm({ ...userForm, action: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="admin-social-user-action">
            <option value="extend_days">Extend days</option>
            <option value="disable">Disable</option>
            <option value="enable">Enable</option>
            <option value="reset">Reset plan</option>
          </select>
          {userForm.action === 'extend_days' && (
            <input type="number" value={userForm.days} onChange={(e) => setUserForm({ ...userForm, days: e.target.value })} placeholder="Days" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          )}
          <button onClick={doUserAction} data-testid="admin-social-user-apply" className="px-3 py-1.5 rounded-lg bg-fuchsia-500 text-white text-xs font-black">Apply</button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 pt-2">
          {users.slice(0, 40).map((u) => (
            <div key={u.user_id} className="rounded bg-slate-800/50 p-1.5 text-[10px] flex items-center gap-2" data-testid={`admin-social-user-${u.user_id}`}>
              <div className="flex-1 truncate"><b>{u.user_id}</b> · {u.brand_shop_name || '—'}</div>
              <div className="opacity-70">{u.plan || '—'}</div>
              <div className="text-emerald-300">{(u.plan_expiry || '').slice(0, 10)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ SOCIAL TEMPLATE LIVE PREVIEW (1080×1080 canvas) ============
function SocialTemplateLivePreview({ form }) {
  const cvsRef = useRef(null);
  useEffect(() => {
    const c = cvsRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    c.width = 1080; c.height = 1080;

    // Background: image_url takes priority, else gradient
    const drawTextAndFinish = () => {
      // Overlay
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, 0, c.width, c.height);
      // Label / title (top-center)
      if (form.label) {
        ctx.fillStyle = '#fbbf24';
        ctx.font = 'bold 72px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        wrapText(ctx, form.label.toUpperCase(), c.width / 2, 60, c.width - 120, 84, 'center');
      }
      // Sample caption (bottom band)
      const capH = 320;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, c.height - capH, c.width, capH);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 46px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      wrapText(ctx, form.sample_caption || 'Sample caption…', 50, c.height - capH + 40, c.width - 100, 60, 'left');
      // Category chip (top-right)
      if (form.category) {
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fillRect(c.width - 340, 40, 300, 60);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 26px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(form.category).toUpperCase(), c.width - 190, 70);
      }
    };

    if (form.image_url) {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ratio = Math.max(c.width / img.width, c.height / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        ctx.drawImage(img, (c.width - w) / 2, (c.height - h) / 2, w, h);
        drawTextAndFinish();
      };
      img.onerror = () => {
        _paintGradient(ctx, c, form.gradient);
        drawTextAndFinish();
      };
      img.src = form.image_url;
    } else {
      _paintGradient(ctx, c, form.gradient);
      drawTextAndFinish();
    }
  }, [form.label, form.category, form.image_url, form.gradient, form.sample_caption]);
  return <canvas ref={cvsRef} className="w-full h-auto block" data-testid="admin-social-tpl-preview" />;
}

function _paintGradient(ctx, c, gradient) {
  // Parse linear-gradient(angle,color1,color2[,color3])
  const g = ctx.createLinearGradient(0, 0, c.width, c.height);
  const stops = _parseGradientStops(gradient) || ['#7c3aed', '#ec4899'];
  stops.forEach((s, i) => g.addColorStop(i / Math.max(1, stops.length - 1), s));
  ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, c.height);
}

function _parseGradientStops(css) {
  if (!css) return null;
  try {
    const m = String(css).match(/linear-gradient\(([^)]+)\)/i);
    if (!m) return null;
    const parts = m[1].split(',').map((x) => x.trim());
    return parts.filter((p) => p.startsWith('#') || p.startsWith('rgb'));
  } catch { return null; }
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, align = 'left') {
  const words = String(text || '').split(/\s+/);
  let line = ''; let cy = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy); line = w; cy += lineHeight;
    } else { line = test; }
    if (cy > y + lineHeight * 4.5) break;
  }
  if (line) ctx.fillText(line, x, cy);
}


// ============ WEBSHOP ADMIN PANEL ============
function WebShopAdminPanel() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [userForm, setUserForm] = useState({ user_id: '', action: 'extend_days', days: 30, product_limit: 50 });
  const [prodFilter, setProdFilter] = useState('all'); // all | active | inactive
  const load = useCallback(async () => {
    try {
      const [s, cfg, u, p] = await Promise.all([
        api.get('/admin/webshop/stats').catch(() => ({ data: null })),
        api.get('/admin/webshop/settings').catch(() => ({ data: null })),
        api.get('/admin/webshop/users').catch(() => ({ data: { users: [] } })),
        api.get('/admin/webshop/products').catch(() => ({ data: { products: [] } })),
      ]);
      setStats(s.data); setSettings(cfg.data); setUsers(u.data.users || []); setProducts(p.data.products || []);
    } catch { toast.error('Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try { const r = await api.post('/admin/webshop/settings', settings); setSettings(r.data.settings); toast.success('✅ Settings saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doUserAction = async () => {
    if (!userForm.user_id) { toast.error('user_id required'); return; }
    try {
      const body = { user_id: userForm.user_id, action: userForm.action };
      if (userForm.action === 'extend_days') body.days = Number(userForm.days || 30);
      if (userForm.action === 'set_product_limit') body.product_limit = Number(userForm.product_limit || 50);
      await api.post('/admin/webshop/users/action', body); toast.success('✅ Done'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const productAction = async (id, action) => {
    if (action === 'delete' && !window.confirm('Delete this product permanently?')) return;
    try {
      await api.post('/admin/webshop/products/action', { product_id: id, action });
      toast.success('✅ Done'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const filteredProducts = products.filter((p) =>
    prodFilter === 'all' ? true : prodFilter === 'active' ? p.active : !p.active
  );

  if (!settings || !stats) return <div className="text-center text-emerald-300 py-8">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="admin-webshop-panel">
      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        {[
          { k: 'total_users', l: 'Users' }, { k: 'active', l: 'Active' },
          { k: 'trials', l: 'Trials' }, { k: 'paid', l: 'Paid' },
          { k: 'products', l: 'Products' }, { k: 'ai_uses', l: 'AI uses' },
        ].map((m) => (
          <div key={m.k} className="rounded-xl bg-emerald-500/15 border border-emerald-400/30 p-3 text-center" data-testid={`admin-webshop-stat-${m.k}`}>
            <div className="text-2xl font-black text-emerald-300">{stats[m.k] || 0}</div>
            <div className="text-[10px] opacity-70 uppercase">{m.l}</div>
          </div>
        ))}
      </div>

      {/* Settings */}
      <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-4 space-y-2">
        <div className="text-xs font-black text-emerald-300 uppercase">⚙️ WebShop Settings (Full Control)</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} data-testid="admin-webshop-enabled" /> Enabled</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!settings.ai_enabled} onChange={(e) => setSettings({ ...settings, ai_enabled: e.target.checked })} data-testid="admin-webshop-ai-enabled" /> AI Enabled</label>
          <label className="text-xs flex flex-col">Trial Hours
            <input type="number" value={settings.trial_hours} onChange={(e) => setSettings({ ...settings, trial_hours: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-webshop-trial-hours" /></label>
          <label className="text-xs flex flex-col">Monthly Price (AT)
            <input type="number" value={settings.monthly_price_at} onChange={(e) => setSettings({ ...settings, monthly_price_at: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-webshop-monthly-price" /></label>
          <label className="text-xs flex flex-col">Monthly Days
            <input type="number" value={settings.monthly_days} onChange={(e) => setSettings({ ...settings, monthly_days: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" /></label>
          <label className="text-xs flex flex-col">Yearly Price (AT)
            <input type="number" value={settings.yearly_price_at} onChange={(e) => setSettings({ ...settings, yearly_price_at: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-webshop-yearly-price" /></label>
          <label className="text-xs flex flex-col">Yearly Days
            <input type="number" value={settings.yearly_days} onChange={(e) => setSettings({ ...settings, yearly_days: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" /></label>
          <label className="text-xs flex flex-col">AI Charge (AT/call)
            <input type="number" value={settings.ai_charge_at} onChange={(e) => setSettings({ ...settings, ai_charge_at: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-webshop-ai-charge" /></label>
          <label className="text-xs flex flex-col">Default Product Limit (per user)
            <input type="number" value={settings.default_product_limit || 50} onChange={(e) => setSettings({ ...settings, default_product_limit: Number(e.target.value) })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700" data-testid="admin-webshop-default-product-limit" /></label>
        </div>
        <button onClick={saveSettings} data-testid="admin-webshop-save-settings" className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">💾 Save Settings</button>
      </div>

      {/* User Actions */}
      <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-4 space-y-2">
        <div className="text-xs font-black text-emerald-300 uppercase">👤 User Actions ({users.length} users)</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input value={userForm.user_id} onChange={(e) => setUserForm({ ...userForm, user_id: e.target.value })} placeholder="user_id" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="admin-webshop-user-id" />
          <select value={userForm.action} onChange={(e) => setUserForm({ ...userForm, action: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="admin-webshop-user-action">
            <option value="extend_days">Extend days</option>
            <option value="block">Block</option>
            <option value="unblock">Unblock</option>
            <option value="reset">Reset plan</option>
            <option value="start_trial">Start trial</option>
            <option value="set_product_limit">Set product limit</option>
          </select>
          {userForm.action === 'extend_days' && (
            <input type="number" value={userForm.days} onChange={(e) => setUserForm({ ...userForm, days: e.target.value })} placeholder="Days" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          )}
          {userForm.action === 'set_product_limit' && (
            <input type="number" value={userForm.product_limit} onChange={(e) => setUserForm({ ...userForm, product_limit: e.target.value })} placeholder="Limit" data-testid="admin-webshop-limit-input" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          )}
          <button onClick={doUserAction} data-testid="admin-webshop-user-apply" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">Apply</button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 pt-2">
          {users.slice(0, 60).map((u) => (
            <div key={u.user_id} className="rounded bg-slate-800/50 p-1.5 text-[10px] flex items-center gap-2" data-testid={`admin-webshop-user-${u.user_id}`}>
              <button onClick={() => setUserForm({ ...userForm, user_id: u.user_id })} className="text-emerald-300 underline">use</button>
              <div className="flex-1 truncate"><b>{u.user_id}</b> · {u.shop?.name || '—'}</div>
              <div className="opacity-70">{u.plan || '—'}</div>
              {u.product_limit_override != null && (
                <div className="px-1 rounded bg-cyan-500/30 text-cyan-200 font-black" title="Product limit override">📦 {u.product_limit_override}</div>
              )}
              <div className={u.blocked ? 'text-rose-300' : 'text-emerald-300'}>{u.blocked ? '🚫' : (u.plan_expiry || '').slice(0, 10)}</div>
            </div>
          ))}
          {users.length === 0 && <div className="text-center opacity-60 py-3">No WebShop users yet.</div>}
        </div>
      </div>

      {/* Products moderation */}
      <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black text-emerald-300 uppercase">📦 Products Moderation ({filteredProducts.length})</div>
          <div className="flex gap-1">
            {['all', 'active', 'inactive'].map((f) => (
              <button key={f} onClick={() => setProdFilter(f)} data-testid={`admin-webshop-prod-filter-${f}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-black ${prodFilter === f ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-slate-300'}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {filteredProducts.slice(0, 200).map((p) => (
            <div key={p.id} className="rounded bg-slate-800/50 p-2 flex items-center gap-2" data-testid={`admin-webshop-prod-${p.id}`}>
              {p.image_url ? <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center text-lg">📦</div>}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate">{p.name} {p.audio_url && <span className="text-amber-300">🎙</span>} {p.attachment_url && <span className="text-cyan-300">📎</span>}</div>
                <div className="text-[10px] opacity-70 truncate">₹{p.price} · {p.mode || 'b2c'} · uid: {p.user_id?.slice(0, 8)}…</div>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${p.active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-slate-600/40 text-slate-300'}`}>{p.active ? 'LIVE' : 'OFF'}</span>
              {p.active ? (
                <button onClick={() => productAction(p.id, 'disable')} data-testid={`admin-webshop-prod-disable-${p.id}`} className="text-[10px] px-2 py-0.5 rounded bg-amber-500/30 text-amber-200 font-black">Disable</button>
              ) : (
                <button onClick={() => productAction(p.id, 'enable')} data-testid={`admin-webshop-prod-enable-${p.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/30 text-emerald-200 font-black">Enable</button>
              )}
              <button onClick={() => productAction(p.id, 'delete')} data-testid={`admin-webshop-prod-delete-${p.id}`} className="text-[10px] px-2 py-0.5 rounded bg-rose-500/30 text-rose-200 font-black">Delete</button>
            </div>
          ))}
          {filteredProducts.length === 0 && <div className="text-center opacity-60 py-3">No products in this filter.</div>}
        </div>
      </div>
    </div>
  );
}



// ============ MARKETPLACE ADMIN PANEL ============
function MarketplaceAdminPanel() {
  const [settings, setSettings] = useState(null);
  const [listings, setListings] = useState([]);
  const [cats, setCats] = useState([]);
  const [catForm, setCatForm] = useState({ id: null, name: '', parent: '', image_url: '', icon: '🏷️' });
  const [filter, setFilter] = useState('all'); // all | featured | held
  const load = useCallback(async () => {
    try {
      const [s, ls, c] = await Promise.all([
        api.get('/admin/marketplace/settings'),
        api.get('/admin/marketplace/listings'),
        api.get('/marketplace/categories'),
      ]);
      setSettings(s.data.settings);
      setListings(ls.data.listings || []);
      setCats(c.data.categories || []);
    } catch { toast.error('Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { const r = await api.post('/admin/marketplace/settings', settings); setSettings(r.data.settings); toast.success('✅ Saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const act = async (body) => {
    try { await api.post('/admin/marketplace/action', body); toast.success('✅ Done'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const rows = listings.filter((l) => filter === 'all' ? true : filter === 'featured' ? l.featured : !l.active);

  if (!settings) return <div className="text-center py-8 opacity-60">⏳ Loading…</div>;
  return (
    <div className="space-y-4" data-testid="admin-marketplace-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-xl bg-purple-500/15 border border-purple-400/40 p-3 text-center" data-testid="admin-mkt-stat-listings">
          <div className="text-2xl font-black text-purple-300">{listings.length}</div>
          <div className="text-[10px] opacity-70 uppercase">Listings</div>
        </div>
        <div className="rounded-xl bg-yellow-500/15 border border-yellow-400/40 p-3 text-center">
          <div className="text-2xl font-black text-yellow-300">{listings.filter((l) => l.featured).length}</div>
          <div className="text-[10px] opacity-70 uppercase">Featured</div>
        </div>
        <div className="rounded-xl bg-emerald-500/15 border border-emerald-400/40 p-3 text-center">
          <div className="text-2xl font-black text-emerald-300">{listings.filter((l) => l.verified).length}</div>
          <div className="text-[10px] opacity-70 uppercase">Verified</div>
        </div>
        <div className="rounded-xl bg-rose-500/15 border border-rose-400/40 p-3 text-center">
          <div className="text-2xl font-black text-rose-300">{listings.filter((l) => !l.active).length}</div>
          <div className="text-[10px] opacity-70 uppercase">Held</div>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-purple-400/40 bg-purple-500/5 p-4 space-y-2">
        <div className="text-xs font-black text-purple-300 uppercase">⚙️ Marketplace Controls</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label className="text-xs flex flex-col">Traffic baseline
            <input type="number" value={settings.traffic_baseline || 100} onChange={(e) => setSettings({ ...settings, traffic_baseline: Number(e.target.value) })} data-testid="admin-mkt-baseline" className="px-2 py-1 rounded bg-slate-800 border border-slate-700" /></label>
          <label className="flex items-center gap-2 text-xs mt-4">
            <input type="checkbox" checked={!!settings.fluctuation} onChange={(e) => setSettings({ ...settings, fluctuation: e.target.checked })} data-testid="admin-mkt-fluct" /> Live fluctuation
          </label>
          <label className="flex items-center gap-2 text-xs mt-4">
            <input type="checkbox" checked={!!settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} data-testid="admin-mkt-enabled" /> Marketplace enabled
          </label>
        </div>
        <button onClick={save} data-testid="admin-mkt-save" className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black">💾 Save</button>
      </div>

      {/* Categories manager */}
      <div className="rounded-2xl border-2 border-purple-400/40 bg-purple-500/5 p-4 space-y-2" data-testid="admin-mkt-cats">
        <div className="text-xs font-black text-purple-300 uppercase">🏷️ Custom Categories &amp; Sub-Categories</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="Name *" data-testid="admin-mkt-cat-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <select value={catForm.parent} onChange={(e) => setCatForm({ ...catForm, parent: e.target.value })} data-testid="admin-mkt-cat-parent" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">(Root category)</option>
            {cats.filter((c) => !c.parent).map((c) => <option key={c.id} value={c.slug}>{c.icon || ''} {c.name}</option>)}
          </select>
          <input value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} placeholder="Icon (emoji)" data-testid="admin-mkt-cat-icon" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={catForm.image_url} onChange={(e) => setCatForm({ ...catForm, image_url: e.target.value })} placeholder="Image URL" data-testid="admin-mkt-cat-image" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {catForm.id && <button onClick={() => setCatForm({ id: null, name: '', parent: '', image_url: '', icon: '🏷️' })} className="py-1.5 rounded bg-slate-700 text-white text-[10px] font-black">✕ Cancel</button>}
          <button onClick={async () => {
                    if (!catForm.name.trim()) { toast.error('Name required'); return; }
                    try { await api.post('/admin/marketplace/categories', catForm); toast.success('✅ Saved'); setCatForm({ id: null, name: '', parent: '', image_url: '', icon: '🏷️' }); load(); }
                    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                  }}
                  data-testid="admin-mkt-cat-save"
                  className={`${catForm.id ? '' : 'col-span-2'} py-1.5 rounded bg-purple-500 text-white text-[10px] font-black`}>{catForm.id ? '💾 Update' : '➕ Add Category'}</button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 pt-2">
          {cats.map((c) => (
            <div key={c.id} className="rounded bg-slate-800/60 p-2 flex items-center gap-2 text-xs" data-testid={`admin-mkt-cat-${c.id}`}>
              {c.image_url && <img src={c.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black">{c.icon || '🏷️'} {c.name} {c.parent && <span className="text-[9px] opacity-70">↳ {c.parent}</span>}</div>
                <div className="text-[9px] opacity-70 truncate">slug: {c.slug}</div>
              </div>
              <button onClick={() => setCatForm({ id: c.id, name: c.name, parent: c.parent || '', image_url: c.image_url || '', icon: c.icon || '🏷️' })} className="p-1 rounded bg-amber-500 text-slate-900" data-testid={`admin-mkt-cat-edit-${c.id}`}><Edit3 size={10} /></button>
              <button onClick={async () => { if (window.confirm('Delete?')) { await api.delete(`/admin/marketplace/categories/${c.id}`); load(); } }} className="p-1 rounded bg-red-900/60" data-testid={`admin-mkt-cat-del-${c.id}`}>🗑</button>
            </div>
          ))}
          {cats.length === 0 && <div className="text-center opacity-60 py-3 text-xs">No custom categories yet.</div>}
        </div>
      </div>

      <div className="rounded-2xl border-2 border-purple-400/40 bg-purple-500/5 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black text-purple-300 uppercase">📦 All Listings ({rows.length})</div>
          <div className="flex gap-1">
            {['all', 'featured', 'held'].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`admin-mkt-filter-${f}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-black ${filter === f ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="max-h-[500px] overflow-y-auto space-y-1">
          {rows.map((l) => (
            <div key={l.id} className="rounded bg-slate-800/60 p-2 flex items-center gap-2 text-xs" data-testid={`admin-mkt-listing-${l.id}`}>
              {l.images?.[0] ? <img src={l.images[0].startsWith('http') ? l.images[0] : `${process.env.REACT_APP_BACKEND_URL || ''}${l.images[0]}`} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 bg-slate-700 rounded flex items-center justify-center text-lg">📦</div>}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black truncate">{l.title} <span className="text-yellow-300">· {l.price_at} AT</span></div>
                <div className="text-[9px] opacity-70 truncate">🏪 {l.shop_name} · slug: {l.shop_slug} · 👁 {l.views || 0} · 💬 {l.leads || 0}</div>
              </div>
              {!l.featured ? (
                <button onClick={() => act({ listing_id: l.id, action: 'feature' })} data-testid={`admin-mkt-feature-${l.id}`} title="Feature" className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500 text-white font-black">⭐ Feature</button>
              ) : (
                <button onClick={() => act({ listing_id: l.id, action: 'unfeature' })} data-testid={`admin-mkt-unfeature-${l.id}`} title="Unfeature" className="text-[9px] px-1.5 py-0.5 rounded bg-slate-600 text-white font-black">✕ Unfeat.</button>
              )}
              {!l.verified && <button onClick={() => act({ store_slug: l.shop_slug, action: 'verify' })} title="Verify shop" data-testid={`admin-mkt-verify-${l.id}`} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-slate-900 font-black">✓ Verify</button>}
              <button onClick={() => act({ listing_id: l.id, action: l.active ? 'hold_listing' : 'release_listing' })} data-testid={`admin-mkt-toggle-${l.id}`} className={`text-[9px] px-1.5 py-0.5 rounded font-black ${l.active ? 'bg-amber-500 text-slate-900' : 'bg-emerald-500 text-slate-900'}`}>{l.active ? 'Hold' : 'Release'}</button>
              <button onClick={() => act({ store_slug: l.shop_slug, action: 'ban_store' })} title="Ban store" data-testid={`admin-mkt-ban-${l.id}`} className="text-[9px] px-1.5 py-0.5 rounded bg-rose-600 text-white font-black">🚫</button>
              <button onClick={() => window.confirm('Delete?') && act({ listing_id: l.id, action: 'delete_listing' })} data-testid={`admin-mkt-delete-${l.id}`} className="p-1 rounded bg-red-900/60">🗑</button>
            </div>
          ))}
          {rows.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No listings.</div>}
        </div>
      </div>
    </div>
  );
}
